// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseURL: "http://localhost:8002" | (string & {});
};

/**
 * AdjustLineItem
 *
 * One line's adjustment from the user. ``reason`` is always mandatory.
 *
 * Exactly one kind of adjustment per item:
 *
 * - **Amount** — ``user_adjusted_to_purchase`` (grams). The UI converts the
 * box count it shows into grams using the line's known ``unit_size``.
 * - **Swap** — ``user_adjusted_product_code`` + ``user_adjusted_boxes``,
 * provided together: a different GFS product and a box count entered
 * directly (the UI can't know the swapped product's case size).
 *
 * The two kinds are mutually exclusive; applying one clears the other on the
 * stored line.
 */
export type AdjustLineItem = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Reason
   */
  reason: string;
  /**
   * User Adjusted To Purchase
   */
  user_adjusted_to_purchase?: number | null;
  /**
   * User Adjusted Product Code
   */
  user_adjusted_product_code?: string | null;
  /**
   * User Adjusted Boxes
   */
  user_adjusted_boxes?: number | null;
};

/**
 * AdjustOrderRequest
 *
 * User adjusts an order: edit existing lines and/or add new manual items.
 *
 * The order is located by ``order_id`` (returned on the purchase list); each
 * existing line by ``food_id``. ``new_items`` are manual additions not
 * produced by the generator.
 */
export type AdjustOrderRequest = {
  /**
   * Order Id
   */
  order_id: number;
  /**
   * Adjustments
   */
  adjustments?: Array<AdjustLineItem>;
  /**
   * New Items
   */
  new_items?: Array<NewPurchaseItem>;
};

/**
 * Body_auto_tagging_food_ing_gfs_scrapper_csv_post
 */
export type BodyAutoTaggingFoodIngGfsScrapperCsvPost = {
  /**
   * Csv File
   */
  csv_file: Blob | File;
  /**
   * Csv Gfs Categories
   */
  csv_gfs_categories: Blob | File;
  /**
   * Csv Rule Df
   */
  csv_rule_df: Blob | File;
};

/**
 * Body_change_password_auth_change_password_put
 */
export type BodyChangePasswordAuthChangePasswordPut = {
  /**
   * New Password
   */
  new_password: string;
};

/**
 * Body_create_food_tag_food_tag_post
 */
export type BodyCreateFoodTagFoodTagPost = {
  food_tag: FoodTagCreate;
  /**
   * Additional Keywords
   */
  additional_keywords?: Array<string> | null;
};

/**
 * Body_create_ingredient_food_ing_post
 */
export type BodyCreateIngredientFoodIngPost = {
  ingredient: FoodItemCreate;
  supplier_info: FoodItemSupplierInfoCreate;
  nutrition_facts: FoodItemNutritionCreate;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * Equivalent Ingredients
   */
  equivalent_ingredients?: Array<FoodItemIngredientCreateDeep> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * Body_create_user_account_user_account_post
 */
export type BodyCreateUserAccountUserAccountPost = {
  new_user: UserCreate;
  /**
   * Assign Group Ids
   */
  assign_group_ids?: Array<number> | null;
  init_credential?: UserLoginCredentialCreate | null;
  diet_profile?: UserDietProfileCreateDeep | null;
};

/**
 * Body_deep_create_recipe_food_recipe_post
 */
export type BodyDeepCreateRecipeFoodRecipePost = {
  dish_info: FoodItemCreate;
  recipe: FoodRecipeCreateDeep;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * Variants
   */
  variants?: Array<FoodItemRecipeCreateDeep> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * Body_deep_update_recipe_food_item__food_id__recipe__recipe_id__patch
 */
export type BodyDeepUpdateRecipeFoodItemFoodIdRecipeRecipeIdPatch = {
  dish_info?: FoodItemUpdate | null;
  recipe?: FoodRecipeUpdateDeep | null;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * Variants
   */
  variants?: Array<FoodItemUpdateDeepInBatch | FoodItemRecipeCreateDeep> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * Body_extract_replenishment_from_supplier_order_csv_inventory_stock_replenishment_supplier_order_csv_file_post
 */
export type BodyExtractReplenishmentFromSupplierOrderCsvInventoryStockReplenishmentSupplierOrderCsvFilePost =
  {
    /**
     * Order Csv File
     */
    order_csv_file: Blob | File;
  };

/**
 * Body_get_purchasing_guide_from_custom_menus_on_dates_inventory_purchasing_from_custom_menus_on_dates_post
 */
export type BodyGetPurchasingGuideFromCustomMenusOnDatesInventoryPurchasingFromCustomMenusOnDatesPost =
  {
    /**
     * Required Menu Ids On Dates
     */
    required_menu_ids_on_dates: Array<MenusOnDateParams>;
    /**
     * Reserved Menu Ids On Dates
     */
    reserved_menu_ids_on_dates?: Array<MenusOnDateParams>;
    filter_params?: ParamFilterPurchaseGuide | null;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: {
      [key: string]: number;
    } | null;
  };

/**
 * Body_get_purchasing_guide_from_date_range_inventory_purchasing_from_date_range_post
 */
export type BodyGetPurchasingGuideFromDateRangeInventoryPurchasingFromDateRangePost =
  {
    /**
     * Required Date Range
     */
    required_date_range: [string, string];
    /**
     * Reserved Date Range
     */
    reserved_date_range?: [string, string];
    filter_params?: ParamFilterPurchaseGuide | null;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: {
      [key: string]: number;
    } | null;
  };

/**
 * Body_get_purchasing_guide_from_portion_orders_inventory_purchasing_from_portion_orders_post
 */
export type BodyGetPurchasingGuideFromPortionOrdersInventoryPurchasingFromPortionOrdersPost =
  {
    /**
     * Required Dish Portion Orders
     */
    required_dish_portion_orders: Array<DishOrderPortions>;
    /**
     * Reserved Dish Portion Orders
     */
    reserved_dish_portion_orders?: Array<DishOrderPortions>;
    filter_params?: ParamFilterPurchaseGuide | null;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: {
      [key: string]: number;
    } | null;
  };

/**
 * Body_invoice_batch_update_supplier_info_food_supplier_info_invoice_post
 */
export type BodyInvoiceBatchUpdateSupplierInfoFoodSupplierInfoInvoicePost = {
  /**
   * Invoice Csv File
   */
  invoice_csv_file: Blob | File;
};

/**
 * Body_login_for_access_token_auth_token_post
 */
export type BodyLoginForAccessTokenAuthTokenPost = {
  /**
   * Grant Type
   */
  grant_type?: string | null;
  /**
   * User Id
   */
  user_id?: number;
  /**
   * Username
   */
  username?: string;
  /**
   * Scope
   */
  scope?: string;
  /**
   * Client Id
   */
  client_id?: string | null;
  /**
   * Client Secret
   */
  client_secret?: string | null;
};

/**
 * Body_update_ingredient_food_ing__food_id__patch
 */
export type BodyUpdateIngredientFoodIngFoodIdPatch = {
  ingredient?: FoodItemUpdate | null;
  supplier_info?: FoodItemSupplierInfoUpdate | null;
  nutrition_facts?: FoodItemNutritionUpdate | null;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * Equivalent Ingredients
   */
  equivalent_ingredients?: Array<
    FoodItemUpdateDeepInBatch | FoodItemIngredientCreateDeep
  > | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * Body_update_user_diet_profile_diet_profile__user_id__patch
 */
export type BodyUpdateUserDietProfileDietProfileUserIdPatch = {
  updating_user_diet_profile_attributes: UserDietProfileUpdateDeep;
  updating_user_attributes?: UserUpdate | null;
};

/**
 * Body_upload_order_csv_inventory_gfs_ordering_purchasing_order_csv_post
 */
export type BodyUploadOrderCsvInventoryGfsOrderingPurchasingOrderCsvPost = {
  /**
   * Order Csv File
   */
  order_csv_file: Blob | File;
  /**
   * Facility Id
   */
  facility_id: number;
  /**
   * Order Date
   */
  order_date: string;
  /**
   * Language
   */
  language?: string | null;
};

/**
 * ChildRecipeCategories
 *
 * Child recipe categories
 */
export enum ChildRecipeCategories {
  /**
   * STATIC
   */
  STATIC = "static",
  /**
   * RUNTIME_HERB
   */
  RUNTIME_HERB = "runtime herb mixing",
  /**
   * RUNTIME_MEP_PREP
   */
  RUNTIME_MEP_PREP = "mise en place preparation",
}

/**
 * ContainerTypes
 *
 * Container type names
 */
export enum ContainerTypes {
  /**
   * ANY
   */
  ANY = "any",
  /**
   * BOTTLE
   */
  BOTTLE = "bottle",
  /**
   * CALIBRATION_BOTTLE
   */
  CALIBRATION_BOTTLE = "calibration bottle",
  /**
   * IKEA_WITH_BIG_SPOUT
   */
  IKEA_WITH_BIG_SPOUT = "ikea_with_big_spout",
  /**
   * SEASONING
   */
  SEASONING = "seasoning",
  /**
   * SMALL_SEASONING
   */
  SMALL_SEASONING = "small_seasoning",
  /**
   * WATER_BOTTLE
   */
  WATER_BOTTLE = "water_bottle",
  /**
   * WATER_TAB
   */
  WATER_TAB = "water_tab",
  /**
   * OXO
   */
  OXO = "oxo",
  /**
   * LIQUID
   */
  LIQUID = "liquid",
  /**
   * CANISTER
   */
  CANISTER = "canister",
  /**
   * PASTE_BOTTLE
   */
  PASTE_BOTTLE = "paste_bottle",
  /**
   * LIQUID_BOTTLE
   */
  LIQUID_BOTTLE = "liquid_bottle",
  /**
   * COLD_LIQUID_CANISTER
   */
  COLD_LIQUID_CANISTER = "cold_liquid_canister",
  /**
   * EMPTY_COLD_LIQUID
   */
  EMPTY_COLD_LIQUID = "empty_cold_liquid",
  /**
   * GN1_9
   */
  GN1_9 = "GN1_9",
  /**
   * GN1_1
   */
  GN1_1 = "GN1_1",
  /**
   * GN1_2
   */
  GN1_2 = "GN1_2",
  /**
   * GN6_BROWNE
   */
  GN6_BROWNE = "GN6_Browne",
  /**
   * GN6_CAMBRO
   */
  GN6_CAMBRO = "GN6_CAMBRO",
  /**
   * GN1_6_4_CAMBRO
   */
  GN1_6_4_CAMBRO = "GN1/6_4in_CAMBRO",
  /**
   * GN1_1_4
   */
  GN1_1_4 = "GN1/1_4in",
  /**
   * GN1_1_4_HOLED
   */
  GN1_1_4_HOLED = "GN1/1_4in_holed",
  /**
   * GN1_1_PASTRY_SHEET
   */
  GN1_1_PASTRY_SHEET = "GN1/1_pastry_sheet",
  /**
   * GN1_1_2
   */
  GN1_1_2 = "GN1/1_2in",
  /**
   * GN1_2_2
   */
  GN1_2_2 = "GN1/2_2in",
  /**
   * GN1_3_2
   */
  GN1_3_2 = "GN1/3_2in",
}

/**
 * CredentialStatus
 *
 * Status of user credential
 */
export enum CredentialStatus {
  PENDING = "pending",
  ACTIVE = "active",
}

/**
 * DishOrderPortions
 *
 * Portion order of a dish representation
 */
export type DishOrderPortions = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Amount
   */
  amount: number;
  /**
   * Amount Unit
   */
  amount_unit?: "portion";
  /**
   * Serving Date
   */
  serving_date?: string | null;
  /**
   * Serving Time
   */
  serving_time?: string | null;
  /**
   * Menu Name
   */
  menu_name?: string | null;
  /**
   * Item Display Name
   */
  item_display_name?: string | null;
};

/**
 * FoodCookOrigin
 *
 * Where is the food cooked from
 */
export enum FoodCookOrigin {
  OUTSOURCED = "outsourced",
  MANUAL = "manual",
  ROBOT = "robot",
  OVEN = "oven",
}

/**
 * FoodGroup
 *
 * Full food group model
 */
export type FoodGroup = {
  /**
   * Group Name
   */
  group_name?: string | null;
  group_type?: FoodGroupType | null;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Food Items
   */
  food_items?: Array<FoodItemBasicWithUserGroups>;
};

/**
 * FoodGroupCreate
 *
 * Food group model use at creation
 */
export type FoodGroupCreate = {
  /**
   * Group Name
   */
  group_name: string;
  group_type: FoodGroupType;
};

/**
 * FoodGroupKey
 *
 * Food group key model
 */
export type FoodGroupKey = {
  /**
   * Id
   */
  id: number;
  /**
   * Group Name
   */
  group_name?: string | null;
  group_type?: FoodGroupType | null;
};

/**
 * FoodGroupMembersSyncRequest
 *
 * Request body for syncing a food group's members to an exact set
 *
 * ``food_ids`` is the complete target membership: missing links are
 * created and links to any other food are removed. An empty set clears
 * the group.
 */
export type FoodGroupMembersSyncRequest = {
  /**
   * Food Ids
   */
  food_ids: Array<number>;
};

/**
 * FoodGroupType
 *
 * Food group types
 *
 * ``SITE_DISH`` groups the per-site versions of one logical dish. Other
 * grouping axes (e.g. equivalents) may be added as new members later;
 * variant lineage stays on ``FoodItemDB.original_food_id``.
 */
export enum FoodGroupType {
  SITE_DISH = "site_dish",
}

/**
 * FoodGroupUpdate
 *
 * Food group model use at updating
 *
 * Only renaming is supported; ``group_type`` is immutable after creation.
 */
export type FoodGroupUpdate = {
  /**
   * Group Name
   */
  group_name?: string | null;
};

/**
 * FoodItem
 *
 * Full food item model
 */
export type FoodItem = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipe>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
};

/**
 * FoodItemAndEquivalentFoods
 *
 * Food item and equivalent food items
 */
export type FoodItemAndEquivalentFoods = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipe>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Equivalent Food Items
   */
  equivalent_food_items?: Array<FoodItem>;
};

/**
 * FoodItemAndRelatedFoods
 *
 * Food item with both its variant siblings and its is_food-tag equivalents.
 *
 * "Related foods" covers both kinds of group membership a food can have:
 * variant siblings (sharing ``original_food_id``) and equivalents (sharing the
 * same is_food-category tag). Inherits both subtypes' field sets, so a
 * ``FoodItemAndRelatedFoods`` is-a ``FoodItemAndVariantFoods`` and is-a
 * ``FoodItemAndEquivalentFoods`` — callers that expect either subtype can
 * accept it directly without projection. Used as the food item cache's
 * stored shape so a single key read returns the food plus both groups.
 */
export type FoodItemAndRelatedFoods = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipe>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Equivalent Food Items
   */
  equivalent_food_items?: Array<FoodItem>;
  /**
   * Variant Food Items
   */
  variant_food_items?: Array<FoodItem>;
};

/**
 * FoodItemAndUsages
 *
 * Food item enriched with references to where it is used elsewhere.
 */
export type FoodItemAndUsages = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipe>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Used As Ingredient In
   */
  used_as_ingredient_in?: Array<FoodUsageAsIngredient>;
  /**
   * Used In Menus
   */
  used_in_menus?: Array<MenuUsage>;
  /**
   * Used In Menu Loops
   */
  used_in_menu_loops?: Array<MenuLoopUsage>;
};

/**
 * FoodItemAndVariantFoods
 *
 * Food item and its variant food items.
 *
 * A variant food item has its own recipe-level food tags (e.g. food texture,
 * liquid thickness, diet type) but shares lineage with an original food item
 * via FoodItemDB.original_food_id.
 */
export type FoodItemAndVariantFoods = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipe>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Variant Food Items
   */
  variant_food_items?: Array<FoodItem>;
};

/**
 * FoodItemBasic
 *
 * Food item base model
 */
export type FoodItemBasic = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
};

/**
 * FoodItemBasicWithUserGroups
 *
 * Food item basic model with its linked user groups
 */
export type FoodItemBasicWithUserGroups = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
};

/**
 * FoodItemCreate
 *
 * Food item model use at creation
 */
export type FoodItemCreate = {
  /**
   * Food Name
   */
  food_name: string;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
};

/**
 * FoodItemEffectiveTags
 *
 * Response body of the food tags preview
 *
 * ``food_tags`` is what a menu item linked to this food would report under
 * the given settings and overrides; ``removable_tags`` lists the food's
 * own tags a chef may hide, computed regardless of the body.
 */
export type FoodItemEffectiveTags = {
  /**
   * Food Tags
   */
  food_tags?: Array<FoodTagBasic>;
  /**
   * Removable Tags
   */
  removable_tags?: Array<FoodTagBasic>;
};

/**
 * FoodItemEffectiveTagsRequest
 *
 * Optional body of the food tags preview: overrides to try out
 *
 * Keyed on the food (not a saved menu item) so the menu editor can preview
 * an item it hasn't saved yet.
 */
export type FoodItemEffectiveTagsRequest = {
  /**
   * Food Tag Overrides
   */
  food_tag_overrides?: Array<MenuItemFoodTagOverrideCreate>;
};

/**
 * FoodItemIngredientCreateDeep
 *
 * Food item model use at deep creation
 */
export type FoodItemIngredientCreateDeep = {
  /**
   * Food Name
   */
  food_name: string;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  supplier_info: FoodItemSupplierInfoCreate;
  nutrition_facts: FoodItemNutritionCreate;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * FoodItemKey
 *
 * Food item key model
 */
export type FoodItemKey = {
  /**
   * Id
   */
  id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
};

/**
 * FoodItemNutrition
 *
 * Full food labeled nutrients data model
 */
export type FoodItemNutrition = {
  /**
   * Data Source
   */
  data_source: string;
  /**
   * Serving Size
   */
  serving_size: number;
  /**
   * Calories
   */
  calories?: number | null;
  /**
   * Fat
   */
  fat?: number | null;
  /**
   * Saturated Fat
   */
  saturated_fat?: number | null;
  /**
   * Trans Fat
   */
  trans_fat?: number | null;
  /**
   * Cholesterol
   */
  cholesterol?: number | null;
  /**
   * Sodium
   */
  sodium?: number | null;
  /**
   * Carbohydrates
   */
  carbohydrates?: number | null;
  /**
   * Fibre
   */
  fibre?: number | null;
  /**
   * Sugars
   */
  sugars?: number | null;
  /**
   * Protein
   */
  protein?: number | null;
  /**
   * Calcium
   */
  calcium?: number | null;
  /**
   * Iron
   */
  iron?: number | null;
  /**
   * Potassium
   */
  potassium?: number | null;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * FoodItemNutritionCreate
 *
 * Food labeled nutrients data use at creation
 */
export type FoodItemNutritionCreate = {
  /**
   * Data Source
   */
  data_source: string;
  /**
   * Serving Size
   */
  serving_size: number;
  /**
   * Calories
   */
  calories?: number | null;
  /**
   * Fat
   */
  fat?: number | null;
  /**
   * Saturated Fat
   */
  saturated_fat?: number | null;
  /**
   * Trans Fat
   */
  trans_fat?: number | null;
  /**
   * Cholesterol
   */
  cholesterol?: number | null;
  /**
   * Sodium
   */
  sodium?: number | null;
  /**
   * Carbohydrates
   */
  carbohydrates?: number | null;
  /**
   * Fibre
   */
  fibre?: number | null;
  /**
   * Sugars
   */
  sugars?: number | null;
  /**
   * Protein
   */
  protein?: number | null;
  /**
   * Calcium
   */
  calcium?: number | null;
  /**
   * Iron
   */
  iron?: number | null;
  /**
   * Potassium
   */
  potassium?: number | null;
};

/**
 * FoodItemNutritionUpdate
 *
 * Food labeled nutrients data use at updating
 */
export type FoodItemNutritionUpdate = {
  /**
   * Data Source
   */
  data_source?: string | null;
  /**
   * Serving Size
   */
  serving_size?: number | null;
  /**
   * Calories
   */
  calories?: number | null;
  /**
   * Fat
   */
  fat?: number | null;
  /**
   * Saturated Fat
   */
  saturated_fat?: number | null;
  /**
   * Trans Fat
   */
  trans_fat?: number | null;
  /**
   * Cholesterol
   */
  cholesterol?: number | null;
  /**
   * Sodium
   */
  sodium?: number | null;
  /**
   * Carbohydrates
   */
  carbohydrates?: number | null;
  /**
   * Fibre
   */
  fibre?: number | null;
  /**
   * Sugars
   */
  sugars?: number | null;
  /**
   * Protein
   */
  protein?: number | null;
  /**
   * Calcium
   */
  calcium?: number | null;
  /**
   * Iron
   */
  iron?: number | null;
  /**
   * Potassium
   */
  potassium?: number | null;
};

/**
 * FoodItemRecipeCreateDeep
 *
 * Food item model use at deep creation of a recipe food item.
 *
 * Bundles the dish info, recipe definition, and recipe-level tags. Mirrors
 * ``FoodItemIngredientCreateDeep`` (which carries the ingredient shape: supplier and
 * nutrition).
 */
export type FoodItemRecipeCreateDeep = {
  /**
   * Food Name
   */
  food_name: string;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  recipe: FoodRecipeCreateDeep;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * FoodItemReturnType
 *
 * Return-shape options for food item read endpoints.
 *
 * Values are the public query-string contract clients send (e.g.
 * ``?return_type=food and usages``). Inheriting from ``str`` keeps the wire
 * form unchanged and lets FastAPI bind query params directly.
 */
export enum FoodItemReturnType {
  FOOD_AND_EQUIVALENT_FOODS = "food and equivalent foods",
  FOOD_AND_VARIANT_FOODS = "food and variant foods",
  FOOD_AND_RELATED_FOODS = "food and related foods",
  FOOD_AND_USAGES = "food and usages",
  FOOD = "food",
  FOOD_KEY = "food key",
}

/**
 * FoodItemSearchingParams
 *
 * Food item searching parameters
 */
export type FoodItemSearchingParams = {
  /**
   * Keywords
   */
  keywords?: string | null;
  /**
   * Has Food Tag Ids All
   */
  has_food_tag_ids_all?: Array<number> | null;
  /**
   * Has Food Tag Ids Any
   */
  has_food_tag_ids_any?: Array<number> | null;
  /**
   * Has Food Tag Ids None
   */
  has_food_tag_ids_none?: Array<number> | null;
  /**
   * Has Step Types All
   */
  has_step_types_all?: Array<StepType> | null;
  /**
   * Has Step Types Any
   */
  has_step_types_any?: Array<StepType> | null;
  /**
   * Has Step Types None
   */
  has_step_types_none?: Array<StepType> | null;
};

/**
 * FoodItemSupplierInfo
 *
 * Full food supplier model
 */
export type FoodItemSupplierInfo = {
  /**
   * Supplier Id
   */
  supplier_id: string;
  /**
   * Product Code
   */
  product_code: string;
  /**
   * Product Name
   */
  product_name: string;
  /**
   * Unit Size
   */
  unit_size?: number | null;
  /**
   * Pack Size
   */
  pack_size?: number | null;
  /**
   * Pack Price
   */
  pack_price?: number | null;
  /**
   * Supplier Unit Size
   */
  supplier_unit_size: number;
  /**
   * Supplier Unit Size Uom
   */
  supplier_unit_size_uom: string;
  /**
   * Barcode
   */
  barcode?: string | null;
  /**
   * Url
   */
  url?: string | null;
  /**
   * Active
   */
  active?: boolean | null;
  /**
   * Ingredients
   */
  ingredients?: string | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Food Id
   */
  food_id: number;
};

/**
 * FoodItemSupplierInfoCreate
 *
 * Food supplier model use at creation
 */
export type FoodItemSupplierInfoCreate = {
  /**
   * Supplier Id
   */
  supplier_id: string;
  /**
   * Product Code
   */
  product_code: string;
  /**
   * Product Name
   */
  product_name: string;
  /**
   * Unit Size
   */
  unit_size?: number | null;
  /**
   * Pack Size
   */
  pack_size?: number | null;
  /**
   * Pack Price
   */
  pack_price?: number | null;
  /**
   * Supplier Unit Size
   */
  supplier_unit_size: number;
  /**
   * Supplier Unit Size Uom
   */
  supplier_unit_size_uom: string;
  /**
   * Barcode
   */
  barcode?: string | null;
  /**
   * Url
   */
  url?: string | null;
  /**
   * Active
   */
  active?: boolean | null;
  /**
   * Ingredients
   */
  ingredients?: string | null;
};

/**
 * FoodItemSupplierInfoUpdate
 *
 * Food supplier model use at updating
 */
export type FoodItemSupplierInfoUpdate = {
  /**
   * Supplier Id
   */
  supplier_id?: string | null;
  /**
   * Product Code
   */
  product_code?: string | null;
  /**
   * Product Name
   */
  product_name?: string | null;
  /**
   * Unit Size
   */
  unit_size?: number | null;
  /**
   * Pack Size
   */
  pack_size?: number | null;
  /**
   * Pack Price
   */
  pack_price?: number | null;
  /**
   * Supplier Unit Size
   */
  supplier_unit_size?: number | null;
  /**
   * Supplier Unit Size Uom
   */
  supplier_unit_size_uom?: string | null;
  /**
   * Barcode
   */
  barcode?: string | null;
  /**
   * Url
   */
  url?: string | null;
  /**
   * Active
   */
  active?: boolean | null;
  /**
   * Ingredients
   */
  ingredients?: string | null;
};

/**
 * FoodItemSwapUsagesReport
 *
 * What a swap-usages call changed, per usage category.
 */
export type FoodItemSwapUsagesReport = {
  /**
   * Source Food Id
   */
  source_food_id: number;
  /**
   * Source Food Name
   */
  source_food_name: string;
  /**
   * Target Food Id
   */
  target_food_id: number;
  /**
   * Target Food Name
   */
  target_food_name: string;
  /**
   * Swapped Ingredient Usages
   */
  swapped_ingredient_usages?: Array<FoodUsageAsIngredient>;
  /**
   * Swapped Menu Usages
   */
  swapped_menu_usages?: Array<MenuUsage>;
  /**
   * Swapped Menu Loop Usages
   */
  swapped_menu_loop_usages?: Array<MenuLoopUsage>;
};

/**
 * FoodItemSwapUsagesRequest
 *
 * Request body for swapping a food item's live usages to another food.
 */
export type FoodItemSwapUsagesRequest = {
  /**
   * Target Food Id
   */
  target_food_id: number;
};

/**
 * FoodItemTag
 *
 * Full food item tag model
 */
export type FoodItemTag = {
  /**
   * Tag Name
   */
  tag_name?: string | null;
  /**
   * Tag Categories
   */
  tag_categories: Array<FoodTagCategory>;
  /**
   * Tag Description
   */
  tag_description?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Data Source
   */
  data_source?: string | null;
  /**
   * Probability
   */
  probability?: number | null;
  /**
   * Is Variant Identity
   */
  is_variant_identity?: boolean;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  removed_by_user?: UserKey | null;
};

/**
 * FoodItemTagCreateInBatch
 *
 * Food item tag model use at creating in batch
 */
export type FoodItemTagCreateInBatch = {
  /**
   * Data Source
   */
  data_source?: string | null;
  /**
   * Probability
   */
  probability?: number | null;
  /**
   * Is Variant Identity
   */
  is_variant_identity?: boolean;
  /**
   * Tag Id
   */
  tag_id: number;
};

/**
 * FoodItemTagsBatchEditItem
 *
 * One food's tag edit in a batch tag edit request.
 */
export type FoodItemTagsBatchEditItem = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Add Tag Ids
   */
  add_tag_ids?: Array<number>;
  /**
   * Remove Tag Ids
   */
  remove_tag_ids?: Array<number>;
};

/**
 * FoodItemTagsBatchEditRequest
 *
 * Request body of the batch tag edit endpoint.
 */
export type FoodItemTagsBatchEditRequest = {
  /**
   * Items
   */
  items?: Array<FoodItemTagsBatchEditItem>;
};

/**
 * FoodItemTagsBatchEditResponse
 *
 * Response body of the batch tag edit endpoint, one result per item.
 */
export type FoodItemTagsBatchEditResponse = {
  /**
   * Results
   */
  results?: Array<FoodItemTagsBatchEditResult>;
};

/**
 * FoodItemTagsBatchEditResult
 *
 * Per-food outcome of a batch tag edit; ``error`` is set on failure.
 */
export type FoodItemTagsBatchEditResult = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Success
   */
  success: boolean;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * FoodItemUpdate
 *
 * Food item model use at updating
 */
export type FoodItemUpdate = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state?: IngredientState | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life?: string | null;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
};

/**
 * FoodItemUpdateDeepInBatch
 *
 * Food item model use at deep updating in batch
 */
export type FoodItemUpdateDeepInBatch = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state?: IngredientState | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life?: string | null;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
  supplier_info?: FoodItemSupplierInfoUpdate | null;
  nutrition_facts?: FoodItemNutritionUpdate | null;
  recipe?: FoodRecipeUpdateDeep | null;
  /**
   * Food Item Tags
   */
  food_item_tags?: Array<FoodItemTagCreateInBatch> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
  /**
   * Id
   */
  id: number;
};

/**
 * FoodQuantity
 *
 * Food quantity representation
 */
export type FoodQuantity = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Amount
   */
  amount: number;
  amount_unit?: FoodQuantityUnits;
};

/**
 * FoodQuantityAndCost
 *
 * Data model for food quantity and cost detail
 */
export type FoodQuantityAndCost = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Amount
   */
  amount: number;
  amount_unit?: FoodQuantityUnits;
  /**
   * Unit Size
   */
  unit_size?: number | null;
  /**
   * Pack Size
   */
  pack_size?: number | null;
  /**
   * Pack Price
   */
  pack_price?: number | null;
  /**
   * Amount Cost
   */
  amount_cost?: number | null;
};

/**
 * FoodQuantityUnits
 *
 * Food quantity units
 */
export enum FoodQuantityUnits {
  PORTION = "portion",
  BOWL_BATCH = "bowl/batch",
  GRAM = "gram",
}

/**
 * FoodRecipe
 *
 * Recipe model
 */
export type FoodRecipe = {
  /**
   * Portion
   */
  portion: number;
  /**
   * Scalable
   */
  scalable: boolean;
  /**
   * Cooked At Hub
   */
  cooked_at_hub?: boolean | null;
  /**
   * Review Stars
   */
  review_stars?: number | null;
  /**
   * Yield Amount
   */
  yield_amount: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  /**
   * Cuisine
   */
  cuisine?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Updated At Aggregated
   */
  updated_at_aggregated?: string | null;
  /**
   * Mep Presets
   */
  mep_presets?: Array<FoodRecipeMepPresetBasic>;
  /**
   * Steps
   */
  steps?: Array<FoodRecipeStep>;
  /**
   * Total Ingredient Used
   *
   * View only recipe's total ingredient usage
   */
  readonly total_ingredient_used: number;
};

/**
 * FoodRecipeCreateDeep
 *
 * Recipe model use at deep creation
 */
export type FoodRecipeCreateDeep = {
  /**
   * Portion
   */
  portion: number;
  /**
   * Scalable
   */
  scalable: boolean;
  /**
   * Cooked At Hub
   */
  cooked_at_hub?: boolean | null;
  /**
   * Review Stars
   */
  review_stars?: number | null;
  /**
   * Yield Amount
   */
  yield_amount: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  /**
   * Cuisine
   */
  cuisine?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Food Id
   */
  food_id?: number | null;
  /**
   * Steps
   */
  steps?: Array<FoodRecipeStepCreateDeep>;
  /**
   * Mep Presets
   */
  mep_presets?: Array<FoodRecipeMepPresetCreate>;
};

/**
 * Food recipe key model
 */
export type FoodRecipeKey = {
  id: number;
  name?: string | null;
  food_id: number;
  food_name?: string | null;
};

/**
 * FoodRecipeMepPresetBasic
 *
 * Mise En Place dispense settings basic model
 */
export type FoodRecipeMepPresetBasic = {
  executor: ResourceTypeCodes;
  container_type: ContainerTypes;
  store_temperature: StorageTemperature;
  /**
   * Speed Factor
   */
  speed_factor: number;
  /**
   * Mep Group Num
   */
  mep_group_num: number;
};

/**
 * FoodRecipeMepPresetCreate
 *
 * Mise En Place dispense settings model use at creation
 */
export type FoodRecipeMepPresetCreate = {
  executor: ResourceTypeCodes;
  container_type: ContainerTypes;
  store_temperature: StorageTemperature;
  /**
   * Speed Factor
   */
  speed_factor: number;
};

/**
 * FoodRecipeMepPresetUpdateInBatch
 *
 * Mise En Place dispense settings model use at updating in batch
 */
export type FoodRecipeMepPresetUpdateInBatch = {
  executor?: ResourceTypeCodes | null;
  container_type?: ContainerTypes | null;
  store_temperature?: StorageTemperature | null;
  /**
   * Speed Factor
   */
  speed_factor?: number | null;
  /**
   * Mep Group Num
   */
  mep_group_num: number;
};

/**
 * FoodRecipeStep
 *
 * Recipe step model
 */
export type FoodRecipeStep = {
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Step Config
   */
  step_config:
    | FoodRecipeStepIngredient
    | FoodRecipeStepCooking
    | FoodRecipeStepOven
    | FoodRecipeStepHuman
    | FoodRecipeStepSubRecipe;
  /**
   * Successor Step Ids
   */
  successor_step_ids?: Array<number>;
  /**
   * Predecessor Step Ids
   */
  predecessor_step_ids?: Array<number>;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Updated At Aggregated
   */
  updated_at_aggregated?: string | null;
  /**
   * Id
   */
  id: number;
};

/**
 * FoodRecipeStepCooking
 *
 * Cooking step config model
 */
export type FoodRecipeStepCooking = {
  /**
   * Cook Time
   */
  cook_time: number;
  /**
   * Cook Temp
   */
  cook_temp: number;
  /**
   * Cook Speed
   */
  cook_speed: number;
  /**
   * Increase Duration
   */
  increase_duration: number;
  lid_status: LidStatus;
  step_type?: StepType;
};

/**
 * FoodRecipeStepCookingCreate
 *
 * Cooking step config model use at creation
 */
export type FoodRecipeStepCookingCreate = {
  /**
   * Cook Time
   */
  cook_time: number;
  /**
   * Cook Temp
   */
  cook_temp: number;
  /**
   * Cook Speed
   */
  cook_speed: number;
  /**
   * Increase Duration
   */
  increase_duration: number;
  lid_status: LidStatus;
};

/**
 * FoodRecipeStepCookingUpdate
 *
 * Cooking step config model use at updating
 */
export type FoodRecipeStepCookingUpdate = {
  /**
   * Cook Time
   */
  cook_time?: number | null;
  /**
   * Cook Temp
   */
  cook_temp?: number | null;
  /**
   * Cook Speed
   */
  cook_speed?: number | null;
  /**
   * Increase Duration
   */
  increase_duration?: number | null;
  lid_status?: LidStatus | null;
  /**
   * Step Type
   */
  step_type?: "cooking";
};

/**
 * FoodRecipeStepCreateDeep
 *
 * Recipe step model use at deep creation
 */
export type FoodRecipeStepCreateDeep = {
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Step Config
   */
  step_config:
    | FoodRecipeStepIngredientCreate
    | FoodRecipeStepCookingCreate
    | FoodRecipeStepOvenCreate
    | FoodRecipeStepHumanCreate
    | FoodRecipeStepSubRecipeCreate;
  /**
   * Successor Step Ids
   */
  successor_step_ids?: Array<number>;
  /**
   * Predecessor Step Ids
   */
  predecessor_step_ids?: Array<number>;
};

/**
 * FoodRecipeStepHuman
 *
 * Human step config model
 */
export type FoodRecipeStepHuman = {
  /**
   * Instruction
   */
  instruction: string;
  /**
   * Estimate Duration
   */
  estimate_duration: number;
  category?: HumanStepCategories | null;
  step_type?: StepType;
};

/**
 * FoodRecipeStepHumanCreate
 *
 * Human step config model use at creation
 */
export type FoodRecipeStepHumanCreate = {
  /**
   * Instruction
   */
  instruction: string;
  /**
   * Estimate Duration
   */
  estimate_duration: number;
  category?: HumanStepCategories | null;
};

/**
 * FoodRecipeStepHumanUpdate
 *
 * Human step config model use at updating
 */
export type FoodRecipeStepHumanUpdate = {
  /**
   * Instruction
   */
  instruction?: string | null;
  /**
   * Estimate Duration
   */
  estimate_duration?: number | null;
  category?: HumanStepCategories | null;
  /**
   * Step Type
   */
  step_type?: "human";
};

/**
 * FoodRecipeStepIngredient
 *
 * Ingredient step config model
 */
export type FoodRecipeStepIngredient = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Amount
   */
  amount: number;
  send_to: ResourceTypeCodes;
  /**
   * Mep Group Num
   */
  mep_group_num?: number | null;
  /**
   * Ingredient Food Name
   */
  ingredient_food_name: string;
  /**
   * Is Recipe As Ingredient
   */
  is_recipe_as_ingredient: boolean;
  step_type?: StepType;
};

/**
 * FoodRecipeStepIngredientCreate
 *
 * Ingredient step config model use at creation
 */
export type FoodRecipeStepIngredientCreate = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Amount
   */
  amount: number;
  send_to: ResourceTypeCodes;
  /**
   * Mep Group Num
   */
  mep_group_num?: number | null;
};

/**
 * FoodRecipeStepIngredientUpdate
 *
 * Ingredient step config model use at updating
 */
export type FoodRecipeStepIngredientUpdate = {
  /**
   * Food Id
   */
  food_id?: number | null;
  /**
   * Amount
   */
  amount?: number | null;
  send_to?: ResourceTypeCodes | null;
  /**
   * Mep Group Num
   */
  mep_group_num?: number | null;
  /**
   * Step Type
   */
  step_type?: "ingredient";
};

/**
 * FoodRecipeStepOven
 *
 * Oven step config model
 */
export type FoodRecipeStepOven = {
  /**
   * Target Temp
   */
  target_temp?: number | null;
  /**
   * Cook Time
   */
  cook_time?: number | null;
  /**
   * Cook Temp
   */
  cook_temp: number;
  /**
   * Cook Humidity
   */
  cook_humidity: number;
  mode: OvenMode;
  step_type?: StepType;
};

/**
 * FoodRecipeStepOvenCreate
 *
 * Oven step config model use at creation
 */
export type FoodRecipeStepOvenCreate = {
  /**
   * Target Temp
   */
  target_temp?: number | null;
  /**
   * Cook Time
   */
  cook_time?: number | null;
  /**
   * Cook Temp
   */
  cook_temp: number;
  /**
   * Cook Humidity
   */
  cook_humidity: number;
  mode: OvenMode;
};

/**
 * FoodRecipeStepOvenUpdate
 *
 * Oven step config model use at updating
 */
export type FoodRecipeStepOvenUpdate = {
  /**
   * Target Temp
   */
  target_temp?: number | null;
  /**
   * Cook Time
   */
  cook_time?: number | null;
  /**
   * Cook Temp
   */
  cook_temp: number;
  /**
   * Cook Humidity
   */
  cook_humidity?: number | null;
  mode?: OvenMode | null;
  /**
   * Step Type
   */
  step_type?: "oven";
};

/**
 * FoodRecipeStepSubRecipe
 *
 * Sub recipe step config model
 */
export type FoodRecipeStepSubRecipe = {
  sub_recipe_step_category?: ChildRecipeCategories;
  /**
   * Sub Recipe Id
   */
  sub_recipe_id: number;
  /**
   * Sub Recipe Food Name
   */
  sub_recipe_food_name: string;
  step_type?: StepType;
  sub_recipe: FoodRecipe;
};

/**
 * FoodRecipeStepSubRecipeCreate
 *
 * Sub recipe step config model use at creation
 */
export type FoodRecipeStepSubRecipeCreate = {
  sub_recipe_step_category?: ChildRecipeCategories;
  /**
   * Sub Recipe Id
   */
  sub_recipe_id: number;
};

/**
 * FoodRecipeStepSubRecipeUpdate
 *
 * Sub recipe step config model use at updating
 */
export type FoodRecipeStepSubRecipeUpdate = {
  sub_recipe_step_category?: ChildRecipeCategories | null;
  /**
   * Sub Recipe Id
   */
  sub_recipe_id?: number | null;
  /**
   * Step Type
   */
  step_type?: "child_recipe";
};

/**
 * FoodRecipeStepUpdateDeepInBatch
 *
 * Recipe step model use at deep updating in a batch
 */
export type FoodRecipeStepUpdateDeepInBatch = {
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Step Config
   */
  step_config?:
    | FoodRecipeStepIngredientUpdate
    | FoodRecipeStepCookingUpdate
    | FoodRecipeStepOvenUpdate
    | FoodRecipeStepHumanUpdate
    | FoodRecipeStepSubRecipeUpdate
    | null;
  /**
   * Successor Step Ids
   */
  successor_step_ids?: Array<number>;
  /**
   * Predecessor Step Ids
   */
  predecessor_step_ids?: Array<number>;
  /**
   * Id
   */
  id: number;
};

/**
 * FoodRecipeUpdateDeep
 *
 * Recipe model use at deep updating
 */
export type FoodRecipeUpdateDeep = {
  /**
   * Portion
   */
  portion?: number | null;
  /**
   * Scalable
   */
  scalable?: boolean | null;
  /**
   * Cooked At Hub
   */
  cooked_at_hub?: boolean | null;
  /**
   * Review Stars
   */
  review_stars?: number | null;
  /**
   * Yield Amount
   */
  yield_amount?: number | null;
  /**
   * Shelf Life
   */
  shelf_life?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  /**
   * Cuisine
   */
  cuisine?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Steps
   */
  steps?: Array<
    FoodRecipeStepUpdateDeepInBatch | FoodRecipeStepCreateDeep
  > | null;
  /**
   * Mep Presets
   */
  mep_presets?: Array<
    FoodRecipeMepPresetUpdateInBatch | FoodRecipeMepPresetCreate
  > | null;
};

/**
 * FoodTag
 *
 * Full food tag model
 */
export type FoodTag = {
  /**
   * Tag Name
   */
  tag_name?: string | null;
  /**
   * Tag Categories
   */
  tag_categories: Array<FoodTagCategory>;
  /**
   * Tag Description
   */
  tag_description?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * FoodTagBasic
 *
 * Food tag base model
 */
export type FoodTagBasic = {
  /**
   * Tag Name
   */
  tag_name?: string | null;
  /**
   * Tag Categories
   */
  tag_categories: Array<FoodTagCategory>;
  /**
   * Tag Description
   */
  tag_description?: string | null;
  /**
   * Id
   */
  id: number;
};

/**
 * FoodTagCategory
 *
 * Food tag categories
 */
export enum FoodTagCategory {
  IS_FOOD = "is_food",
  FOOD_CATEGORY = "food_category",
  DISH_CATEGORY = "dish_category",
  ALLERGEN = "allergen",
  FOOD_TEXTURE = "food_texture",
  LIQUID_THICKNESS = "liquid_thickness",
  DIET_TYPE = "diet_type",
  PREFERENCE = "preference",
  PROTEIN = "protein",
  APPETIZER = "appetizer",
  SEASONING = "seasoning",
  DESSERT = "dessert",
  SIDE = "side",
  SAUCE = "sauce",
  STARCH = "starch",
  VEGETABLE = "vegetable",
  VARIANT_TYPE = "variant_type",
}

/**
 * FoodTagCreate
 *
 * Food tag model use at creation
 */
export type FoodTagCreate = {
  /**
   * Tag Name
   */
  tag_name: string;
  /**
   * Tag Categories
   */
  tag_categories: Array<FoodTagCategory>;
  /**
   * Tag Description
   */
  tag_description?: string | null;
};

/**
 * FoodTagUpdate
 *
 * Food tag model use at updating
 */
export type FoodTagUpdate = {
  /**
   * Tag Name
   */
  tag_name?: string | null;
  /**
   * Tag Categories
   */
  tag_categories?: Array<FoodTagCategory> | null;
  /**
   * Tag Description
   */
  tag_description?: string | null;
};

/**
 * FoodTagWithTaggedFoodIDs
 *
 * Food tag with tagged food items model
 */
export type FoodTagWithTaggedFoodIds = {
  tag: FoodTag;
  /**
   * Tagged Food Ids
   */
  tagged_food_ids?: Array<number>;
};

/**
 * FoodUsageAsIngredient
 *
 * A food whose recipe uses the target food as an ingredient in one of its steps.
 */
export type FoodUsageAsIngredient = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name: string;
};

/**
 * GordonFoodServiceInvoiceRecord
 *
 * Model of items in GFS invoice CSV file
 *
 * The comments are the column names in the invoice csv file.
 */
export type GordonFoodServiceInvoiceRecord = {
  /**
   * Product Description
   */
  product_description: string;
  /**
   * Item Code
   */
  item_code: string;
  /**
   * Price
   */
  price: number;
  /**
   * Price Uom
   */
  price_uom: string;
  /**
   * Catch Weight
   */
  catch_weight: "Y" | "N";
  /**
   * Quantity Ordered
   */
  quantity_ordered: number;
  /**
   * Quantity Shipped
   */
  quantity_shipped: number;
  /**
   * Total Price
   */
  total_price: number;
  /**
   * Brand
   */
  brand?: string | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Net Weight
   */
  net_weight?: number | null;
  /**
   * Pack Size
   */
  pack_size: number;
  /**
   * Unit Size
   */
  unit_size: number;
  /**
   * Unit Size Uom
   */
  unit_size_uom: string;
  /**
   * Maybe Problematic
   */
  maybe_problematic?: boolean;
  /**
   * Gtin
   */
  gtin: string;
  /**
   * Pack Size Uom
   */
  pack_size_uom: string;
  /**
   * Sale By Unit
   */
  sale_by_unit: "Y" | "N";
};

/**
 * GordonFoodServiceOrderRecord
 *
 * Model of items in GFS order CSV file
 */
export type GordonFoodServiceOrderRecord = {
  /**
   * Product Description
   */
  product_description: string;
  /**
   * Item Code
   */
  item_code: string;
  /**
   * Price
   */
  price: number;
  /**
   * Price Uom
   */
  price_uom: string;
  /**
   * Catch Weight
   */
  catch_weight: "Y" | "N";
  /**
   * Quantity Ordered
   */
  quantity_ordered: number;
  /**
   * Quantity Shipped
   */
  quantity_shipped: number;
  /**
   * Total Price
   */
  total_price: number;
  /**
   * Brand
   */
  brand?: string | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Net Weight
   */
  net_weight: number;
  /**
   * Pack Size
   */
  pack_size: number;
  /**
   * Unit Size
   */
  unit_size: number;
  /**
   * Unit Size Uom
   */
  unit_size_uom: string;
  /**
   * Maybe Problematic
   */
  maybe_problematic?: boolean;
  /**
   * Unit
   */
  unit: string;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
  /**
   * Detail
   */
  detail?: Array<ValidationError>;
};

/**
 * HumanStepCategories
 *
 * Human step categories
 */
export enum HumanStepCategories {
  /**
   * OTHERS
   */
  OTHERS = "others",
  /**
   * TAKE_OUT
   */
  TAKE_OUT = "take out",
  /**
   * DISPENSE
   */
  DISPENSE = "dispense",
  /**
   * PACKING_PLATING
   */
  PACKING_PLATING = "packing/plating",
}

/**
 * IngredientState
 *
 * Ingredient state options
 */
export enum IngredientState {
  /**
   * SOLID
   */
  SOLID = "solid",
  /**
   * POWDER
   */
  POWDER = "powder",
  /**
   * LIQUID
   */
  LIQUID = "liquid",
  /**
   * PASTE
   */
  PASTE = "paste",
  /**
   * FOAM
   */
  FOAM = "foam",
  /**
   * NONE
   */
  NONE = "",
}

/**
 * InventoryUpdateItem
 *
 * One ingredient's inventory update from the chef. Amounts in grams.
 */
export type InventoryUpdateItem = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * User Confirmed Stock Level
   */
  user_confirmed_stock_level?: number | null;
  /**
   * User Adjusted To Purchase
   */
  user_adjusted_to_purchase?: number | null;
  /**
   * Notes
   */
  notes?: string | null;
};

/**
 * InventoryUpdateRequest
 *
 * Chef submits current inventory for one or more ingredients.
 */
export type InventoryUpdateRequest = {
  /**
   * Order Id
   */
  order_id: number;
  /**
   * Updates
   */
  updates: Array<InventoryUpdateItem>;
};

/**
 * LidStatus
 *
 * Cooking machine lid status and/or cooking step lid requirements
 */
export enum LidStatus {
  /**
   * NO_MATTER
   */
  NO_MATTER = "no matter",
  /**
   * OPEN
   */
  OPEN = "open",
  /**
   * CLOSE
   */
  CLOSE = "close",
}

/**
 * MealOrderItemBasicWithMenuItemBasicWithFoodItemBasic
 *
 * The basic meal order item model plus menu item basic and food item basic information
 */
export type MealOrderItemBasicWithMenuItemBasicWithFoodItemBasic = {
  /**
   * Menu Item Id
   */
  menu_item_id: number;
  /**
   * Quantity
   */
  quantity: number;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Id
   */
  id: number;
  menu_item: MenuItemBasicWithFoodItemBasic;
};

/**
 * MealOrderItemCreate
 *
 * Meal order item model used at creation
 */
export type MealOrderItemCreate = {
  /**
   * Menu Item Id
   */
  menu_item_id: number;
  /**
   * Quantity
   */
  quantity: number;
  /**
   * Notes
   */
  notes?: string | null;
};

/**
 * MealOrderItemUpdateDeep
 *
 * Meal order item model used at deep updating or in a batch updating
 */
export type MealOrderItemUpdateDeep = {
  /**
   * Menu Item Id
   */
  menu_item_id?: number | null;
  /**
   * Quantity
   */
  quantity?: number | null;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Id
   */
  id?: number | null;
};

/**
 * MealOrderTicket
 *
 * The full meal order ticket model
 */
export type MealOrderTicket = {
  /**
   * User Id
   */
  user_id: number;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * User Diet Profile
   */
  user_diet_profile: UserDietProfile | UserBasic;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKey>;
  /**
   * Items
   */
  items?: Array<MealOrderItemBasicWithMenuItemBasicWithFoodItemBasic>;
};

/**
 * MealOrderTicketCreateDeep
 *
 * Meal order ticket model use at deep creation
 */
export type MealOrderTicketCreateDeep = {
  /**
   * User Id
   */
  user_id: number;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Items
   */
  items?: Array<MealOrderItemCreate>;
};

/**
 * MealOrderTicketUpdateDeep
 *
 * Meal order ticket model use at deep updating
 */
export type MealOrderTicketUpdateDeep = {
  /**
   * User Id
   */
  user_id?: number | null;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Items
   */
  items?: Array<MealOrderItemUpdateDeep>;
};

/**
 * Menu
 *
 * The menu model
 */
export type Menu = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time: string;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Sections
   */
  sections: Array<MenuOptionGroupBasicWithItems>;
  /**
   * User Groups
   */
  user_groups: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItem;
  } | null;
};

/**
 * The menu model
 */
export type MenuBasic = {
  name?: string | null;
  serving_datetime?: string | null;
  publish_prior_to_serving_time: string;
  cap_finalization_prior_to_serving_time: string;
  id: number;
};

/**
 * MenuCreateDeep
 *
 * The menu model including sections use at creation
 */
export type MenuCreateDeep = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time: string;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time: string;
  /**
   * Sections
   */
  sections?: Array<MenuOptionGroupCreateDeep>;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * MenuInLoop
 *
 * The menu in loop model
 */
export type MenuInLoop = {
  /**
   * Serving Time
   */
  serving_time: string;
  /**
   * Looping Day Index
   */
  looping_day_index: number;
  menu: Menu;
};

/**
 * MenuInLoopCreate
 *
 * The menu in loop create model
 */
export type MenuInLoopCreate = {
  /**
   * Serving Time
   */
  serving_time: string;
  /**
   * Menu Id
   */
  menu_id: number;
};

/**
 * MenuItemBasicWithFoodItemBasic
 *
 * The menu item basic model plus food item basic information
 */
export type MenuItemBasicWithFoodItemBasic = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Cap
   */
  cap: number;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Price
   */
  portion_price: number;
  /**
   * Id
   */
  id: number;
  food_item: FoodItemBasic;
};

/**
 * MenuItemBasicWithSides
 *
 * The menu item basic model plus sides
 */
export type MenuItemBasicWithSides = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Cap
   */
  cap: number;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Price
   */
  portion_price: number;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodTagBasic>;
  /**
   * Food Tag Overrides
   */
  food_tag_overrides?: Array<MenuItemFoodTagOverride>;
  food_item: FoodItemKey;
  /**
   * Food Recipe Portion
   */
  food_recipe_portion?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Side Option Groups
   */
  side_option_groups?: Array<MenuOptionGroupBasicWithItems>;
  /**
   * Option Type
   */
  option_type?: "menu item";
};

/**
 * MenuItemCreateDeep
 *
 * The menu item model including sides use at creation
 */
export type MenuItemCreateDeep = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Cap
   */
  cap: number;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Price
   */
  portion_price: number;
  /**
   * Side Option Groups
   */
  side_option_groups?: Array<MenuOptionGroupCreateDeep> | null;
  /**
   * Food Tag Overrides
   */
  food_tag_overrides?: Array<MenuItemFoodTagOverrideCreate>;
};

/**
 * MenuItemFoodTagOverride
 *
 * One override record on a menu item, as read back
 *
 * ``food_tags`` are the tags behind ``tag_ids``, in the same order.
 */
export type MenuItemFoodTagOverride = {
  override_action?: MenuItemFoodTagOverrideAction;
  tag_category?: FoodTagCategory | null;
  /**
   * Tag Ids
   */
  tag_ids?: Array<number>;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodTagBasic>;
};

/**
 * MenuItemFoodTagOverrideAction
 *
 * What an override record does to the food's tags on a menu item
 *
 * ``ADD`` is reserved: adding tags at menu level is deferred.
 */
export enum MenuItemFoodTagOverrideAction {
  REMOVE = "remove",
  KEEP_ONLY = "keep_only",
}

/**
 * MenuItemFoodTagOverrideCreate
 *
 * One override record on a menu item, as sent by the client
 */
export type MenuItemFoodTagOverrideCreate = {
  override_action?: MenuItemFoodTagOverrideAction;
  tag_category?: FoodTagCategory | null;
  /**
   * Tag Ids
   */
  tag_ids?: Array<number>;
};

/**
 * MenuItemUpdateDeepInBatch
 *
 * The menu item model including sides use at batch updating
 */
export type MenuItemUpdateDeepInBatch = {
  /**
   * Food Id
   */
  food_id?: number | null;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Cap
   */
  cap?: number | null;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Price
   */
  portion_price?: number | null;
  /**
   * Side Option Groups
   */
  side_option_groups?: Array<
    MenuOptionGroupUpdateDeepInBatch | MenuOptionGroupCreateDeep
  > | null;
  /**
   * Food Tag Overrides
   */
  food_tag_overrides?: Array<MenuItemFoodTagOverrideCreate> | null;
  /**
   * Id
   */
  id: number;
  /**
   * Option Type
   */
  option_type?: "menu item";
};

/**
 * MenuLoop
 *
 * The menu loop model
 */
export type MenuLoop = {
  /**
   * Name
   */
  name: string;
  /**
   * Start Date
   */
  start_date: string;
  /**
   * End Date
   */
  end_date: string;
  /**
   * Days Per Cycle
   */
  days_per_cycle: number;
  /**
   * Id
   */
  id: number;
  /**
   * Menu Links
   */
  menu_links: Array<MenuInLoop>;
  /**
   * Skip Dates
   */
  skip_dates?: Array<MenuLoopSpecialDateBasic>;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItem;
  } | null;
};

/**
 * MenuLoopBasic
 *
 * The menu loop base model
 */
export type MenuLoopBasic = {
  /**
   * Name
   */
  name: string;
  /**
   * Start Date
   */
  start_date: string;
  /**
   * End Date
   */
  end_date: string;
  /**
   * Days Per Cycle
   */
  days_per_cycle: number;
  /**
   * Id
   */
  id: number;
};

/**
 * MenuLoopCopyParams
 *
 * Menu loop copy parameters
 */
export type MenuLoopCopyParams = {
  /**
   * New Name
   */
  new_name: string;
  /**
   * Site User Group Id
   */
  site_user_group_id: number;
  /**
   * Skip Copy Special Dates
   */
  skip_copy_special_dates?: boolean;
  /**
   * Copy Menu Templates
   */
  copy_menu_templates?: boolean;
  /**
   * Start Date
   */
  start_date?: string | null;
  /**
   * End Date
   */
  end_date?: string | null;
};

/**
 * MenuLoopCreateDeep
 *
 * The menu loop model use at deep creation
 */
export type MenuLoopCreateDeep = {
  /**
   * Name
   */
  name: string;
  /**
   * Start Date
   */
  start_date: string;
  /**
   * End Date
   */
  end_date: string;
  /**
   * Menu Links
   */
  menu_links: Array<Array<MenuInLoopCreate>>;
  /**
   * Menu Links A La Carte
   */
  menu_links_a_la_carte?: Array<MenuInLoopCreate> | null;
  /**
   * Skip Dates
   */
  skip_dates?: Array<MenuLoopSpecialDateCreate> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * MenuLoopSearchingParams
 *
 * Menu loop searching parameters
 */
export type MenuLoopSearchingParams = {
  /**
   * Keywords
   */
  keywords?: string | null;
  /**
   * Linked User Group Ids All
   */
  linked_user_group_ids_all?: Array<number> | null;
  /**
   * Linked User Group Ids Any
   */
  linked_user_group_ids_any?: Array<number> | null;
  /**
   * Linked User Group Ids None
   */
  linked_user_group_ids_none?: Array<number> | null;
};

/**
 * MenuLoopSpecialDateBasic
 *
 * Menu loop special date basic model
 */
export type MenuLoopSpecialDateBasic = {
  /**
   * Description
   */
  description?: string | null;
  /**
   * Special Date
   */
  special_date: string;
};

/**
 * MenuLoopSpecialDateCreate
 *
 * Menu loop special date use during creation
 */
export type MenuLoopSpecialDateCreate = {
  /**
   * Description
   */
  description?: string | null;
  /**
   * Special Date
   */
  special_date: string;
};

/**
 * MenuLoopUpdateDeep
 *
 * The menu loop model use at deep updating
 */
export type MenuLoopUpdateDeep = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Start Date
   */
  start_date?: string | null;
  /**
   * End Date
   */
  end_date?: string | null;
  /**
   * Menu Links
   */
  menu_links?: Array<Array<MenuInLoopCreate>> | null;
  /**
   * Menu Links A La Carte
   */
  menu_links_a_la_carte?: Array<MenuInLoopCreate> | null;
  /**
   * Skip Dates
   */
  skip_dates?: Array<MenuLoopSpecialDateCreate> | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * MenuLoopUsage
 *
 * A menu loop that references the target food via one of its menus.
 */
export type MenuLoopUsage = {
  /**
   * Menu Loop Id
   */
  menu_loop_id: number;
  /**
   * Menu Loop Name
   */
  menu_loop_name: string;
  /**
   * Site User Groups
   */
  site_user_groups?: Array<SiteUserGroupUsage>;
};

/**
 * MenuOptionGroupBasicWithItems
 *
 * The menu option group model plus items
 */
export type MenuOptionGroupBasicWithItems = {
  /**
   * Name
   */
  name: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Options
   */
  options: Array<MenuItemBasicWithSides | MenuOptionGroupBasicWithItems>;
  /**
   * Option Type
   */
  option_type?: "option group";
};

/**
 * MenuOptionGroupCreateDeep
 *
 * The menu option group model including items use at creation
 */
export type MenuOptionGroupCreateDeep = {
  /**
   * Name
   */
  name: string;
  /**
   * Options
   */
  options?: Array<MenuItemCreateDeep | MenuOptionGroupCreateDeep>;
};

/**
 * MenuOptionGroupUpdateDeepInBatch
 *
 * The menu option group model including items use at batch updating
 */
export type MenuOptionGroupUpdateDeepInBatch = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Options
   */
  options?: Array<
    | MenuItemUpdateDeepInBatch
    | MenuItemCreateDeep
    | MenuOptionGroupUpdateDeepInBatch
    | MenuOptionGroupCreateDeep
  > | null;
  /**
   * Id
   */
  id: number;
  /**
   * Option Type
   */
  option_type?: "option group";
};

/**
 * MenuPreview
 *
 * The future menu from a menu loop use for preview
 */
export type MenuPreview = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time: string;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Sections
   */
  sections: Array<MenuOptionGroupBasicWithItems>;
  /**
   * User Groups
   */
  user_groups: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItem;
  } | null;
  menu_loop: MenuLoopBasic;
};

/**
 * MenuSearchingParams
 *
 * Menu searching parameters
 */
export type MenuSearchingParams = {
  /**
   * Keywords
   */
  keywords?: string | null;
  /**
   * Linked User Group Ids All
   */
  linked_user_group_ids_all?: Array<number> | null;
  /**
   * Linked User Group Ids Any
   */
  linked_user_group_ids_any?: Array<number> | null;
  /**
   * Linked User Group Ids None
   */
  linked_user_group_ids_none?: Array<number> | null;
};

/**
 * MenuUpdateDeep
 *
 * The menu model including sections use at updating
 */
export type MenuUpdateDeep = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time?: string | null;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time?: string | null;
  /**
   * Sections
   */
  sections?: Array<
    MenuOptionGroupUpdateDeepInBatch | MenuOptionGroupCreateDeep
  > | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
};

/**
 * MenuUpdateDeepInBatch
 *
 * Menu model use at deep updating in a batch
 */
export type MenuUpdateDeepInBatch = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time?: string | null;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time?: string | null;
  /**
   * Sections
   */
  sections?: Array<
    MenuOptionGroupUpdateDeepInBatch | MenuOptionGroupCreateDeep
  > | null;
  /**
   * User Group Ids
   */
  user_group_ids?: Array<number> | null;
  /**
   * Id
   */
  id: number;
};

/**
 * MenuUsage
 *
 * A menu that references the target food as a menu item.
 */
export type MenuUsage = {
  /**
   * Menu Id
   */
  menu_id: number;
  /**
   * Menu Name
   */
  menu_name: string;
};

/**
 * MenusOnDateParams
 *
 * Represent menu IDs used on a given date
 */
export type MenusOnDateParams = {
  /**
   * Date
   */
  date: string;
  /**
   * Menu Ids
   */
  menu_ids?: Array<number>;
};

/**
 * NewPurchaseItem
 *
 * A manually-added purchase line not generated from the menu.
 *
 * The operator supplies a GFS product code and a box count (stored verbatim
 * in ``user_adjusted_boxes``, like a swap). ``food_id`` is optional — when
 * given it links an existing Jasper food; otherwise the line is free-form.
 * ``food_name`` defaults to the product code. ``reason`` is mandatory.
 */
export type NewPurchaseItem = {
  /**
   * Gfs Product Code
   */
  gfs_product_code: string;
  /**
   * Boxes
   */
  boxes: number;
  /**
   * Reason
   */
  reason: string;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Food Id
   */
  food_id?: number | null;
};

/**
 * OrderDetailResponse
 *
 * Purchase order with all ingredient lines.
 */
export type OrderDetailResponse = {
  /**
   * Id
   */
  id: number;
  /**
   * Facility Id
   */
  facility_id: number;
  /**
   * Order Date
   */
  order_date: string;
  /**
   * Delivery Date
   */
  delivery_date: string;
  /**
   * Covers From
   */
  covers_from: string;
  /**
   * Covers To
   */
  covers_to: string;
  /**
   * Exported At
   */
  exported_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Lines
   */
  lines?: Array<OrderLineResponse>;
};

/**
 * OrderLineResponse
 *
 * One ingredient line in a purchase order. All amounts are in grams.
 */
export type OrderLineResponse = {
  /**
   * Food Id
   */
  food_id?: number | null;
  /**
   * Food Name
   */
  food_name: string;
  /**
   * Gfs Product Code
   */
  gfs_product_code: string;
  /**
   * Unit Size
   */
  unit_size: number;
  /**
   * Pack Price Cents
   */
  pack_price_cents?: number | null;
  /**
   * Required Amount
   */
  required_amount: number;
  /**
   * Reserved Amount
   */
  reserved_amount: number;
  /**
   * User Confirmed Stock Level
   */
  user_confirmed_stock_level?: number | null;
  /**
   * User Confirmed Stock Level At
   */
  user_confirmed_stock_level_at?: string | null;
  /**
   * To Purchase
   */
  to_purchase?: number | null;
  /**
   * User Adjusted To Purchase
   */
  user_adjusted_to_purchase?: number | null;
  /**
   * User Adjusted Product Code
   */
  user_adjusted_product_code?: string | null;
  /**
   * User Adjusted Boxes
   */
  user_adjusted_boxes?: number | null;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Added By User Id
   */
  added_by_user_id?: number | null;
  /**
   * Id
   */
  id: number;
};

/**
 * OrderSummaryResponse
 *
 * Purchase order summary (no lines) for list views.
 */
export type OrderSummaryResponse = {
  /**
   * Id
   */
  id: number;
  /**
   * Facility Id
   */
  facility_id: number;
  /**
   * Order Date
   */
  order_date: string;
  /**
   * Delivery Date
   */
  delivery_date: string;
  /**
   * Covers From
   */
  covers_from: string;
  /**
   * Covers To
   */
  covers_to: string;
  /**
   * Exported At
   */
  exported_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * OvenMode
 *
 * Oven modes
 */
export enum OvenMode {
  /**
   * COMBI
   */
  COMBI = "combi",
  /**
   * STEAM
   */
  STEAM = "steam",
  /**
   * CONVECTION
   */
  CONVECTION = "convection",
}

/**
 * PaginatedSearchResults[FoodItemAndRelatedFoods]
 */
export type PaginatedSearchResultsFoodItemAndRelatedFoods = {
  /**
   * Data
   */
  data: Array<unknown>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[FoodItemKey]
 */
export type PaginatedSearchResultsFoodItemKey = {
  /**
   * Data
   */
  data: Array<FoodItemKey>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[FoodItem]
 */
export type PaginatedSearchResultsFoodItem = {
  /**
   * Data
   */
  data: Array<FoodItem>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[FoodQuantity]
 */
export type PaginatedSearchResultsFoodQuantity = {
  /**
   * Data
   */
  data: Array<FoodQuantity>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[FoodRecipeKey]
 */
export type PaginatedSearchResultsFoodRecipeKey = {
  /**
   * Data
   */
  data: Array<FoodRecipeKey>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[FoodRecipe]
 */
export type PaginatedSearchResultsFoodRecipe = {
  /**
   * Data
   */
  data: Array<FoodRecipe>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[MenuBasic]
 */
export type PaginatedSearchResultsMenuBasic = {
  /**
   * Data
   */
  data: Array<MenuBasic>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[MenuLoopBasic]
 */
export type PaginatedSearchResultsMenuLoopBasic = {
  /**
   * Data
   */
  data: Array<MenuLoopBasic>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[MenuLoop]
 */
export type PaginatedSearchResultsMenuLoop = {
  /**
   * Data
   */
  data: Array<MenuLoop>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * PaginatedSearchResults[Menu]
 */
export type PaginatedSearchResultsMenu = {
  /**
   * Data
   */
  data: Array<Menu>;
  /**
   * Total Counts
   */
  total_counts?: number | null;
  /**
   * Page Index
   */
  page_index?: number | null;
  /**
   * Page Size
   */
  page_size?: number | null;
};

/**
 * ParamFilterPurchaseGuide
 *
 * How to filter ingredient purchase guide output
 */
export type ParamFilterPurchaseGuide = {
  /**
   * Food Cook Origin Any
   */
  food_cook_origin_any?: Array<FoodCookOrigin> | null;
  /**
   * Food Categories
   */
  food_categories?: Array<string> | null;
  /**
   * Food Ids
   */
  food_ids?: Array<number> | null;
  return_data_type?: PurchasingGuideDataType;
};

/**
 * PasswordResetConfirm
 *
 * Body of the password-reset confirm endpoint
 */
export type PasswordResetConfirm = {
  /**
   * Token
   */
  token: string;
};

/**
 * PasswordResetRequest
 *
 * Body of the password-reset request endpoint
 */
export type PasswordResetRequest = {
  /**
   * Email
   */
  email: string;
};

/**
 * Permission
 *
 * The full Permission model
 */
export type Permission = {
  /**
   * Identifier
   */
  identifier?: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Users
   */
  users?: Array<UserKey>;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupBasic>;
};

/**
 * PermissionBasic
 *
 * The permission basic model
 */
export type PermissionBasic = {
  /**
   * Identifier
   */
  identifier?: string;
  /**
   * Id
   */
  id: number;
};

/**
 * PermissionCreate
 *
 * The Permission model used for creating
 */
export type PermissionCreate = {
  /**
   * Identifier
   */
  identifier?: string;
};

/**
 * PermissionUpdate
 *
 * The Permission model used for updating
 */
export type PermissionUpdate = {
  /**
   * Identifier
   */
  identifier: string;
};

/**
 * ProcurementSlot
 *
 * A single order/delivery/coverage triplet.
 */
export type ProcurementSlot = {
  /**
   * Order Day
   */
  order_day:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  /**
   * Delivery Day
   */
  delivery_day:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  /**
   * Covers
   */
  covers: Array<
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday"
  >;
};

/**
 * PurchaseListItem
 *
 * One ingredient row in the purchase list. All amounts are in grams.
 */
export type PurchaseListItem = {
  /**
   * Food Id
   */
  food_id?: number | null;
  /**
   * Food Name
   */
  food_name: string;
  /**
   * Gfs Product Code
   */
  gfs_product_code: string;
  /**
   * Unit Size
   */
  unit_size: number;
  /**
   * Pack Price Cents
   */
  pack_price_cents?: number | null;
  /**
   * Required Amount
   */
  required_amount: number;
  /**
   * Reserved Amount
   */
  reserved_amount: number;
  /**
   * User Confirmed Stock Level
   */
  user_confirmed_stock_level?: number | null;
  /**
   * User Confirmed Stock Level At
   */
  user_confirmed_stock_level_at?: string | null;
  /**
   * To Purchase
   */
  to_purchase?: number | null;
  /**
   * User Adjusted To Purchase
   */
  user_adjusted_to_purchase?: number | null;
  /**
   * User Adjusted Product Code
   */
  user_adjusted_product_code?: string | null;
  /**
   * User Adjusted Boxes
   */
  user_adjusted_boxes?: number | null;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Added By User Id
   */
  added_by_user_id?: number | null;
  /**
   * Food Category
   */
  food_category?: string;
  storage_temp?: StorageTemperature | null;
  /**
   * Stock Level
   */
  stock_level?: number | null;
  /**
   * Usages
   */
  usages?: Array<PurchasingGuideUsage>;
};

/**
 * PurchaseListResponse
 *
 * Full purchase list for one order event.
 */
export type PurchaseListResponse = {
  /**
   * Order Id
   */
  order_id: number;
  /**
   * Facility Id
   */
  facility_id: number;
  /**
   * Facility Name
   */
  facility_name: string;
  /**
   * Order Date
   */
  order_date: string;
  /**
   * Delivery Date
   */
  delivery_date: string;
  /**
   * Covers From
   */
  covers_from: string;
  /**
   * Covers To
   */
  covers_to: string;
  /**
   * Status
   */
  status?: string;
  /**
   * Items
   */
  items: Array<PurchaseListItem>;
};

/**
 * PurchasingGuide
 *
 * The food item purchasing guide
 */
export type PurchasingGuide = {
  /**
   * Data
   */
  data: Array<PurchasingGuideItem | PurchasingGuideItemAndFoodItem>;
  /**
   * Warnings
   */
  warnings?: string | null;
};

/**
 * PurchasingGuideDataType
 *
 * The return data type for purchasing guide items
 */
export enum PurchasingGuideDataType {
  GUIDE = "guide",
  GUIDE_AND_FOOD = "guide and food",
}

/**
 * PurchasingGuideItem
 *
 * The food item purchasing guiding item
 */
export type PurchasingGuideItem = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Main Food Id
   */
  main_food_id?: number | null;
  /**
   * Supplier Product Code
   */
  supplier_product_code?: string | null;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Required Amount
   */
  required_amount: number | number;
  /**
   * Reserved Amount
   */
  reserved_amount: number | number;
  /**
   * Stock Level
   */
  stock_level: number | number;
  /**
   * To Buy Amount
   */
  to_buy_amount: number | number;
  /**
   * To Buy Boxes
   */
  to_buy_boxes?: number | null;
  /**
   * Usages
   */
  usages?: Array<PurchasingGuideUsage>;
};

/**
 * PurchasingGuideItemAndFoodItem
 *
 * The food item purchasing guiding item with all food item details
 */
export type PurchasingGuideItemAndFoodItem = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Main Food Id
   */
  main_food_id?: number | null;
  /**
   * Supplier Product Code
   */
  supplier_product_code?: string | null;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Required Amount
   */
  required_amount: number | number;
  /**
   * Reserved Amount
   */
  reserved_amount: number | number;
  /**
   * Stock Level
   */
  stock_level: number | number;
  /**
   * To Buy Amount
   */
  to_buy_amount: number | number;
  /**
   * To Buy Boxes
   */
  to_buy_boxes?: number | null;
  /**
   * Usages
   */
  usages?: Array<PurchasingGuideUsage>;
  food_item?: FoodItem | null;
};

/**
 * PurchasingGuideUsage
 *
 * Source usage detail showing which menu item contributed to a purchasing guide item
 */
export type PurchasingGuideUsage = {
  /**
   * Serving Date
   */
  serving_date?: string | null;
  /**
   * Serving Time
   */
  serving_time?: string | null;
  /**
   * Menu Name
   */
  menu_name?: string | null;
  /**
   * Menu Item Food Id
   */
  menu_item_food_id: number;
  /**
   * Menu Item Display Name
   */
  menu_item_display_name?: string | null;
  /**
   * Portions
   */
  portions: number | number;
  /**
   * Amount
   */
  amount: number | number;
};

/**
 * RefreshOrderRequest
 *
 * Request to discard current draft and re-fetch from Jasper.
 */
export type RefreshOrderRequest = {
  /**
   * Facility Id
   */
  facility_id: number;
  /**
   * Order Date
   */
  order_date: string;
};

/**
 * ResourceTypeCodes
 *
 * Resource type codes
 */
export enum ResourceTypeCodes {
  /**
   * UNKNOWN
   */
  UNKNOWN = 0,
  /**
   * COOKING_MACHINE
   */
  COOKING_MACHINE = 1,
  /**
   * OVEN
   */
  OVEN = 2,
  /**
   * SCALE
   */
  SCALE = 3,
  /**
   * ROBOTIC_ARM
   */
  ROBOTIC_ARM = 4,
  /**
   * LIQUID_DISPENSER
   */
  LIQUID_DISPENSER = 5,
  /**
   * HUMAN
   */
  HUMAN = 6,
  /**
   * FRIDGE
   */
  FRIDGE = 7,
  /**
   * CANISTER_DISPENSER
   */
  CANISTER_DISPENSER = 8,
  /**
   * PASTE_DISPENSER
   */
  PASTE_DISPENSER = 9,
  /**
   * COLD_LIQUID_DISPENSER
   */
  COLD_LIQUID_DISPENSER = 10,
  /**
   * BOWL_AND_LID_STATION
   */
  BOWL_AND_LID_STATION = 11,
}

/**
 * SiteProfile
 *
 * The full site profile model
 */
export type SiteProfile = {
  /**
   * Timezone
   */
  timezone?: string;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * User Group Id
   */
  user_group_id: number;
};

/**
 * SiteProfileBasic
 *
 * Minimal site profile shape (no timestamps, no user_group_id) suitable
 * for embedding in API responses where those bookkeeping fields are
 * redundant — e.g. ``UserGroupKeyWithSiteProfile.site_profile``.
 */
export type SiteProfileBasic = {
  /**
   * Timezone
   */
  timezone?: string;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number;
};

/**
 * SiteProfileUpdate
 *
 * The site profile model use at updating
 */
export type SiteProfileUpdate = {
  /**
   * Timezone
   */
  timezone?: string | null;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number | null;
};

/**
 * SiteUserGroupUsage
 *
 * A site user group attached to a menu loop that references the target food.
 */
export type SiteUserGroupUsage = {
  /**
   * User Group Id
   */
  user_group_id: number;
  /**
   * Group Name
   */
  group_name: string;
};

/**
 * StepType
 *
 * Step type names and the data class binding
 */
export enum StepType {
  /**
   * COOKING
   */
  COOKING = "cooking",
  /**
   * INGREDIENT
   */
  INGREDIENT = "ingredient",
  /**
   * OVEN
   */
  OVEN = "oven",
  /**
   * HUMAN
   */
  HUMAN = "human",
  /**
   * CHILD_RECIPE
   */
  CHILD_RECIPE = "child_recipe",
}

/**
 * StockDepletion
 *
 * Stock depletion record model
 */
export type StockDepletion = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Replenishment Record Id
   */
  replenishment_record_id: number;
  /**
   * Id
   */
  id: number;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * StockReplenishment
 *
 * Stock replenishment record model
 */
export type StockReplenishment = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Supplier Record Id
   */
  supplier_record_id: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Total Price
   */
  total_price: number;
  /**
   * Id
   */
  id: number;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Depletion Records
   */
  depletion_records?: Array<StockDepletion>;
};

/**
 * StockReplenishmentCreate
 *
 * Stock replenishment record model use at creation
 */
export type StockReplenishmentCreate = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Supplier Record Id
   */
  supplier_record_id: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Total Price
   */
  total_price: number;
};

/**
 * StockReplenishmentCreateWithFoodItem
 *
 * Model of replenishment use at creation plus the food item data attached
 */
export type StockReplenishmentCreateWithFoodItem = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Supplier Record Id
   */
  supplier_record_id: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Total Price
   */
  total_price: number;
  food_item: FoodItem;
};

/**
 * StorageTemperature
 *
 * storage temperature options
 */
export enum StorageTemperature {
  /**
   * ROOM
   */
  ROOM = "room",
  /**
   * FRIDGE
   */
  FRIDGE = "fridge",
  /**
   * FROZEN
   */
  FROZEN = "frozen",
  /**
   * FRIDGE_ROOM
   */
  FRIDGE_ROOM = "fridge/room",
  /**
   * ROOM_FRIDGE
   */
  ROOM_FRIDGE = "room/fridge",
  /**
   * FROZEN_FRIDGE
   */
  FROZEN_FRIDGE = "frozen/fridge",
}

/**
 * SupplierId
 *
 * Controlled vocabulary of supplier identifiers.
 *
 * Shared across modules: jasper_food_index supplier records and
 * jasper_inventory supply profiles both identify suppliers by these
 * values (stored as plain strings in the database). Extend this enum when
 * onboarding a new supplier — the columns stay VARCHAR, so no migration
 * is needed.
 */
export enum SupplierId {
  GFS = "GFS",
  SYSCO = "Sysco",
  GRM = "GRM",
  _0 = "0",
}

/**
 * SupplyOrderCsv
 *
 * API output model for an uploaded supplier order CSV.
 *
 * ``download_url`` / ``expires_at`` are injected at read time (a short-lived
 * presigned GET URL) and are never persisted.
 */
export type SupplyOrderCsv = {
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Supply Order Id
   */
  supply_order_id: number;
  /**
   * Object Key
   */
  object_key: string;
  /**
   * Original Filename
   */
  original_filename: string;
  /**
   * Content Type
   */
  content_type: string;
  /**
   * Byte Size
   */
  byte_size: number;
  /**
   * Language
   */
  language?: string | null;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Uploaded By User Id
   */
  uploaded_by_user_id: number;
  /**
   * Download Url
   */
  download_url?: string | null;
  /**
   * Expires At
   */
  expires_at?: string | null;
};

/**
 * SupplyOrderCsvAnalysis
 *
 * API output model for a stored order-CSV coverage analysis.
 *
 * ``csv_original_filename`` / ``csv_uploaded_at`` identify which upload the
 * analysis covers; they are injected at read time and never persisted.
 */
export type SupplyOrderCsvAnalysis = {
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Supply Order Csv Id
   */
  supply_order_csv_id: number;
  /**
   * Supply Order Id
   */
  supply_order_id: number;
  /**
   * Status
   */
  status: string;
  /**
   * Error Message
   */
  error_message?: string | null;
  /**
   * N Theory
   */
  n_theory?: number | null;
  /**
   * N Actual
   */
  n_actual?: number | null;
  /**
   * N In Both
   */
  n_in_both?: number | null;
  /**
   * N Exact
   */
  n_exact?: number | null;
  /**
   * N Over
   */
  n_over?: number | null;
  /**
   * N Under
   */
  n_under?: number | null;
  /**
   * N Forgotten
   */
  n_forgotten?: number | null;
  /**
   * N Unexpected
   */
  n_unexpected?: number | null;
  /**
   * N Short Shipped
   */
  n_short_shipped?: number | null;
  /**
   * Item Coverage
   */
  item_coverage?: number | null;
  /**
   * Expected Total Cents
   */
  expected_total_cents?: number | null;
  /**
   * Order Total Cents
   */
  order_total_cents?: number | null;
  /**
   * Dollar Coverage
   */
  dollar_coverage?: number | null;
  /**
   * Per Code
   */
  per_code?: Array<unknown> | null;
  /**
   * Computed At
   */
  computed_at: string;
  /**
   * Csv Original Filename
   */
  csv_original_filename?: string | null;
  /**
   * Csv Uploaded At
   */
  csv_uploaded_at?: string | null;
};

/**
 * SupplyProfileCreateDeep
 *
 * Supply profile model used at deep creation
 */
export type SupplyProfileCreateDeep = {
  /**
   * Name
   */
  name: string;
  /**
   * Jasper Site Id
   */
  jasper_site_id: number;
  supplier_id?: SupplierId;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number;
  /**
   * Timezone
   */
  timezone?: string;
  /**
   * Order Slots
   */
  order_slots: Array<ProcurementSlot>;
  /**
   * Order Email Recipients
   */
  order_email_recipients?: Array<string>;
  /**
   * Ingredient Food Ids
   */
  ingredient_food_ids?: Array<number>;
};

/**
 * SupplyProfileResponse
 *
 * Procurement profile info returned by the API.
 */
export type SupplyProfileResponse = {
  /**
   * Name
   */
  name: string;
  /**
   * Jasper Site Id
   */
  jasper_site_id: number;
  supplier_id?: SupplierId | null;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number;
  /**
   * Timezone
   */
  timezone?: string;
  /**
   * Id
   */
  id: number;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Order Slots
   */
  order_slots: Array<ProcurementSlot>;
  /**
   * Order Email Recipients
   */
  order_email_recipients: Array<string>;
  /**
   * Ingredients
   */
  ingredients?: Array<SupplyProfileTrackedIngredient>;
};

/**
 * SupplyProfileTrackedIngredient
 *
 * Output shape for a single tracked ingredient on a supply profile.
 *
 * Carries the link row's ``food_id`` and ``updated_at`` plus the
 * linked food's ``food_name`` (resolved via the ORM relationship and
 * exposed as a computed_field). Designed to grow as more per-link
 * metadata is surfaced without changing the top-level response shape.
 */
export type SupplyProfileTrackedIngredient = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Food Name
   *
   * Resolve the linked food item's name via the ORM relationship.
   */
  readonly food_name: string | null;
};

/**
 * SupplyProfileUpdateDeep
 *
 * Supply profile model used at deep updating
 */
export type SupplyProfileUpdateDeep = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Jasper Site Id
   */
  jasper_site_id?: number | null;
  supplier_id?: SupplierId | null;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number | null;
  /**
   * Timezone
   */
  timezone?: string | null;
  /**
   * Is Active
   */
  is_active?: boolean | null;
  /**
   * Order Slots
   */
  order_slots?: Array<ProcurementSlot> | null;
  /**
   * Order Email Recipients
   */
  order_email_recipients?: Array<string> | null;
  /**
   * Ingredient Food Ids
   */
  ingredient_food_ids?: Array<number> | null;
};

/**
 * Token
 *
 * The token model
 */
export type Token = {
  /**
   * Access Token
   */
  access_token: string;
  /**
   * Token Type
   */
  token_type: string;
  user: UserRead;
  credential_status: CredentialStatus;
};

/**
 * UpcomingOrderResponse
 *
 * One upcoming order date with its schedule info and status.
 */
export type UpcomingOrderResponse = {
  /**
   * Order Date
   */
  order_date: string;
  /**
   * Delivery Date
   */
  delivery_date: string;
  /**
   * Covers From
   */
  covers_from: string;
  /**
   * Covers To
   */
  covers_to: string;
  /**
   * Status
   */
  status: string;
  /**
   * Order Id
   */
  order_id?: number | null;
};

/**
 * UserBasic
 *
 * User base model
 */
export type UserBasic = {
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Ethnicity
   */
  ethnicity?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Gender
   */
  gender?: string | null;
  /**
   * Weight
   */
  weight?: number | null;
  /**
   * Photo Link
   */
  photo_link?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
  /**
   * Id
   */
  id: number;
};

/**
 * UserCreate
 *
 * User model use at creation
 */
export type UserCreate = {
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Ethnicity
   */
  ethnicity?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Gender
   */
  gender?: string | null;
  /**
   * Weight
   */
  weight?: number | null;
  /**
   * Photo Link
   */
  photo_link?: string | null;
  /**
   * Is Activated
   */
  is_activated: boolean;
};

/**
 * UserDietProfile
 *
 * The full user diet profile model
 */
export type UserDietProfile = {
  /**
   * Like
   */
  like?: Array<string>;
  /**
   * Dislike
   */
  dislike?: Array<string>;
  /**
   * Portion Size
   */
  portion_size?: string | null;
  /**
   * Supplements
   */
  supplements?: string;
  /**
   * Supplement Flavour Preference
   */
  supplement_flavour_preference?: string;
  /**
   * Supplement Frequency
   */
  supplement_frequency?: string;
  /**
   * Adaptive Needs
   */
  adaptive_needs?: string;
  /**
   * Room Number
   */
  room_number: string;
  /**
   * Dining Area
   */
  dining_area?: string | null;
  /**
   * Breakfast Notes
   */
  breakfast_notes?: string | null;
  /**
   * Lunch Notes
   */
  lunch_notes?: string | null;
  /**
   * Supper Notes
   */
  supper_notes?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Ethnicity
   */
  ethnicity?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Gender
   */
  gender?: string | null;
  /**
   * Weight
   */
  weight?: number | null;
  /**
   * Photo Link
   */
  photo_link?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Allergens
   */
  allergens?: Array<FoodTagBasic>;
  /**
   * Diet Types
   */
  diet_types?: Array<FoodTagBasic>;
  food_texture?: FoodTagBasic | null;
  liquid_thickness?: FoodTagBasic | null;
  /**
   * Updated By User Id
   */
  updated_by_user_id: number;
};

/**
 * UserDietProfileCreateDeep
 *
 * The user diet profile model use at deep creation
 */
export type UserDietProfileCreateDeep = {
  /**
   * Like
   */
  like?: Array<string>;
  /**
   * Dislike
   */
  dislike?: Array<string>;
  /**
   * Portion Size
   */
  portion_size?: string | null;
  /**
   * Supplements
   */
  supplements?: string;
  /**
   * Supplement Flavour Preference
   */
  supplement_flavour_preference?: string;
  /**
   * Supplement Frequency
   */
  supplement_frequency?: string;
  /**
   * Adaptive Needs
   */
  adaptive_needs?: string;
  /**
   * Room Number
   */
  room_number: string;
  /**
   * Dining Area
   */
  dining_area?: string | null;
  /**
   * Breakfast Notes
   */
  breakfast_notes?: string | null;
  /**
   * Lunch Notes
   */
  lunch_notes?: string | null;
  /**
   * Supper Notes
   */
  supper_notes?: string | null;
  /**
   * Allergens
   */
  allergens?: Array<number>;
  /**
   * Diet Types
   */
  diet_types?: Array<number>;
  /**
   * Food Texture
   */
  food_texture?: number | null;
  /**
   * Liquid Thickness
   */
  liquid_thickness?: number | null;
};

/**
 * UserDietProfileUpdateDeep
 *
 * The user diet profile model use at deep updating
 */
export type UserDietProfileUpdateDeep = {
  /**
   * Like
   */
  like?: Array<string>;
  /**
   * Dislike
   */
  dislike?: Array<string>;
  /**
   * Portion Size
   */
  portion_size?: string | null;
  /**
   * Supplements
   */
  supplements?: string;
  /**
   * Supplement Flavour Preference
   */
  supplement_flavour_preference?: string;
  /**
   * Supplement Frequency
   */
  supplement_frequency?: string;
  /**
   * Adaptive Needs
   */
  adaptive_needs?: string;
  /**
   * Room Number
   */
  room_number?: string;
  /**
   * Dining Area
   */
  dining_area?: string | null;
  /**
   * Breakfast Notes
   */
  breakfast_notes?: string | null;
  /**
   * Lunch Notes
   */
  lunch_notes?: string | null;
  /**
   * Supper Notes
   */
  supper_notes?: string | null;
  /**
   * Allergens
   */
  allergens?: Array<number>;
  /**
   * Diet Types
   */
  diet_types?: Array<number>;
  /**
   * Food Texture
   */
  food_texture?: number | null;
  /**
   * Liquid Thickness
   */
  liquid_thickness?: number | null;
};

/**
 * UserGroup
 *
 * The full user group model
 */
export type UserGroup = {
  /**
   * Group Name
   */
  group_name?: string | null;
  group_category: UserGroupCategory;
  /**
   * Group Description
   */
  group_description?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Members
   */
  members?: Array<UserKeyAndGroups>;
  /**
   * Permissions
   */
  permissions?: Array<Permission>;
  site_profile?: SiteProfile | null;
};

/**
 * UserGroupBasic
 *
 * The user group base model
 */
export type UserGroupBasic = {
  /**
   * Group Name
   */
  group_name?: string | null;
  group_category: UserGroupCategory;
  /**
   * Group Description
   */
  group_description?: string | null;
  /**
   * Id
   */
  id: number;
};

/**
 * UserGroupCategory
 *
 * User group categories
 */
export enum UserGroupCategory {
  DEPARTMENT = "department",
  FLOOR = "floor",
  ROLE = "role",
  SITE = "site",
}

/**
 * UserGroupCreate
 *
 * The user group model use at creation
 */
export type UserGroupCreate = {
  /**
   * Group Name
   */
  group_name: string;
  group_category: UserGroupCategory;
  /**
   * Group Description
   */
  group_description?: string | null;
};

/**
 * UserGroupKey
 *
 * The user group key model
 */
export type UserGroupKey = {
  /**
   * Id
   */
  id: number;
  group_category: UserGroupCategory;
  /**
   * Group Name
   */
  group_name?: string | null;
};

/**
 * UserGroupKeyWithSiteProfile
 *
 * User group key augmented with the linked SiteProfile, when present.
 *
 * Used in API responses (e.g. ``MenuLoop.user_groups``) where consumers
 * want to read the site's timezone (and, in the future, locale /
 * geolocation) alongside the user_group key without a second request.
 * Non-SITE entries simply have ``site_profile = None``.
 *
 * Populated automatically from ``UserGroupDB.site_profile`` when SQLModel
 * validates a ``UserGroupDB`` into this model.
 */
export type UserGroupKeyWithSiteProfile = {
  /**
   * Id
   */
  id: number;
  group_category: UserGroupCategory;
  /**
   * Group Name
   */
  group_name?: string | null;
  site_profile?: SiteProfileBasic | null;
};

/**
 * UserGroupPermissionDB
 *
 * The user group permission db model
 */
export type UserGroupPermissionDb = {
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * User Group Id
   */
  user_group_id: number;
  /**
   * Permission Id
   */
  permission_id: number;
};

/**
 * UserGroupUpdate
 *
 * The user group model use at updating
 */
export type UserGroupUpdate = {
  /**
   * Group Name
   */
  group_name?: string;
  group_category?: UserGroupCategory;
  /**
   * Group Description
   */
  group_description?: string | null;
};

/**
 * UserKey
 *
 * User minimal key model
 */
export type UserKey = {
  /**
   * Id
   */
  id: number;
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
};

/**
 * UserKeyAndGroups
 *
 * User minimal key and groups model
 */
export type UserKeyAndGroups = {
  /**
   * Id
   */
  id: number;
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKey>;
};

/**
 * UserLoginCredentialCreate
 *
 * The user login credential model use at creating
 */
export type UserLoginCredentialCreate = {
  /**
   * Username
   */
  username: string;
  /**
   * Password
   */
  password: string;
};

/**
 * UserPermissionDB
 *
 * The user permission db model
 */
export type UserPermissionDb = {
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Permission Id
   */
  permission_id: number;
  /**
   * User Id
   */
  user_id: number;
};

/**
 * UserRead
 *
 * User model use at reading (expose externally)
 */
export type UserRead = {
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Ethnicity
   */
  ethnicity?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Gender
   */
  gender?: string | null;
  /**
   * Weight
   */
  weight?: number | null;
  /**
   * Photo Link
   */
  photo_link?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean | null;
  /**
   * Id
   */
  id: number;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKey>;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Permissions
   */
  permissions?: Array<PermissionBasic>;
};

/**
 * UserUpdate
 *
 * User model use at updating
 */
export type UserUpdate = {
  /**
   * Full Name
   */
  full_name?: string;
  /**
   * Ethnicity
   */
  ethnicity?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Gender
   */
  gender?: string | null;
  /**
   * Weight
   */
  weight?: number | null;
  /**
   * Photo Link
   */
  photo_link?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
};

/**
 * ValidationError
 */
export type ValidationError = {
  /**
   * Location
   */
  loc: Array<string | number>;
  /**
   * Message
   */
  msg: string;
  /**
   * Error Type
   */
  type: string;
  /**
   * Input
   */
  input?: unknown;
  /**
   * Context
   */
  ctx?: {
    [key: string]: unknown;
  };
};

/**
 * Body_login_for_access_token_auth_token_post
 */
export type BodyLoginForAccessTokenAuthTokenPostWritable = {
  /**
   * Grant Type
   */
  grant_type?: string | null;
  /**
   * User Id
   */
  user_id?: number;
  /**
   * Username
   */
  username?: string;
  /**
   * Password
   */
  password: string;
  /**
   * Scope
   */
  scope?: string;
  /**
   * Client Id
   */
  client_id?: string | null;
  /**
   * Client Secret
   */
  client_secret?: string | null;
};

/**
 * FoodItem
 *
 * Full food item model
 */
export type FoodItemWritable = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipeWritable>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
};

/**
 * FoodItemAndEquivalentFoods
 *
 * Food item and equivalent food items
 */
export type FoodItemAndEquivalentFoodsWritable = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipeWritable>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Equivalent Food Items
   */
  equivalent_food_items?: Array<FoodItemWritable>;
};

/**
 * FoodItemAndRelatedFoods
 *
 * Food item with both its variant siblings and its is_food-tag equivalents.
 *
 * "Related foods" covers both kinds of group membership a food can have:
 * variant siblings (sharing ``original_food_id``) and equivalents (sharing the
 * same is_food-category tag). Inherits both subtypes' field sets, so a
 * ``FoodItemAndRelatedFoods`` is-a ``FoodItemAndVariantFoods`` and is-a
 * ``FoodItemAndEquivalentFoods`` — callers that expect either subtype can
 * accept it directly without projection. Used as the food item cache's
 * stored shape so a single key read returns the food plus both groups.
 */
export type FoodItemAndRelatedFoodsWritable = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipeWritable>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Equivalent Food Items
   */
  equivalent_food_items?: Array<FoodItemWritable>;
  /**
   * Variant Food Items
   */
  variant_food_items?: Array<FoodItemWritable>;
};

/**
 * FoodItemAndUsages
 *
 * Food item enriched with references to where it is used elsewhere.
 */
export type FoodItemAndUsagesWritable = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipeWritable>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Used As Ingredient In
   */
  used_as_ingredient_in?: Array<FoodUsageAsIngredient>;
  /**
   * Used In Menus
   */
  used_in_menus?: Array<MenuUsage>;
  /**
   * Used In Menu Loops
   */
  used_in_menu_loops?: Array<MenuLoopUsage>;
};

/**
 * FoodItemAndVariantFoods
 *
 * Food item and its variant food items.
 *
 * A variant food item has its own recipe-level food tags (e.g. food texture,
 * liquid thickness, diet type) but shares lineage with an original food item
 * via FoodItemDB.original_food_id.
 */
export type FoodItemAndVariantFoodsWritable = {
  /**
   * Food Name
   */
  food_name?: string | null;
  state: IngredientState;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  storage_temp?: StorageTemperature | null;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Portion Size
   */
  portion_size?: number | null;
  /**
   * Original Food Id
   */
  original_food_id?: number | null;
  /**
   * Id
   */
  id: number;
  /**
   * Checked By Nutritionist User Id
   */
  checked_by_nutritionist_user_id?: number | null;
  /**
   * Checked By Chef User Id
   */
  checked_by_chef_user_id?: number | null;
  /**
   * Checked By Engineer User Id
   */
  checked_by_engineer_user_id?: number | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Is Activated
   */
  is_activated?: boolean;
  /**
   * Food Tags
   */
  food_tags?: Array<FoodItemTag>;
  /**
   * Food Nutrition Records
   */
  food_nutrition_records?: Array<FoodItemNutrition> | null;
  /**
   * Food Suppliers
   */
  food_suppliers?: Array<FoodItemSupplierInfo>;
  /**
   * Food Recipes
   */
  food_recipes?: Array<FoodRecipeWritable>;
  updated_by_user?: UserKey | null;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Food Groups
   */
  food_groups?: Array<FoodGroupKey>;
  /**
   * Portion Cost
   */
  portion_cost?: number | null;
  /**
   * Portion Cost Warnings
   */
  portion_cost_warnings?: string | null;
  /**
   * Portion Cost Breakdown
   */
  portion_cost_breakdown?: Array<FoodQuantityAndCost>;
  /**
   * Variant Food Items
   */
  variant_food_items?: Array<FoodItemWritable>;
};

/**
 * FoodRecipe
 *
 * Recipe model
 */
export type FoodRecipeWritable = {
  /**
   * Portion
   */
  portion: number;
  /**
   * Scalable
   */
  scalable: boolean;
  /**
   * Cooked At Hub
   */
  cooked_at_hub?: boolean | null;
  /**
   * Review Stars
   */
  review_stars?: number | null;
  /**
   * Yield Amount
   */
  yield_amount: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Dispense Category
   */
  dispense_category?: string | null;
  /**
   * Cuisine
   */
  cuisine?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Id
   */
  id: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Updated At Aggregated
   */
  updated_at_aggregated?: string | null;
  /**
   * Mep Presets
   */
  mep_presets?: Array<FoodRecipeMepPresetBasic>;
  /**
   * Steps
   */
  steps?: Array<FoodRecipeStepWritable>;
};

/**
 * FoodRecipeStep
 *
 * Recipe step model
 */
export type FoodRecipeStepWritable = {
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Step Config
   */
  step_config:
    | FoodRecipeStepIngredient
    | FoodRecipeStepCooking
    | FoodRecipeStepOven
    | FoodRecipeStepHuman
    | FoodRecipeStepSubRecipeWritable;
  /**
   * Successor Step Ids
   */
  successor_step_ids?: Array<number>;
  /**
   * Predecessor Step Ids
   */
  predecessor_step_ids?: Array<number>;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Updated At Aggregated
   */
  updated_at_aggregated?: string | null;
  /**
   * Id
   */
  id: number;
};

/**
 * FoodRecipeStepSubRecipe
 *
 * Sub recipe step config model
 */
export type FoodRecipeStepSubRecipeWritable = {
  sub_recipe_step_category?: ChildRecipeCategories;
  /**
   * Sub Recipe Id
   */
  sub_recipe_id: number;
  /**
   * Sub Recipe Food Name
   */
  sub_recipe_food_name: string;
  step_type?: StepType;
  sub_recipe: FoodRecipeWritable;
};

/**
 * Menu
 *
 * The menu model
 */
export type MenuWritable = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time: string;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Sections
   */
  sections: Array<MenuOptionGroupBasicWithItems>;
  /**
   * User Groups
   */
  user_groups: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItemWritable;
  } | null;
};

/**
 * MenuInLoop
 *
 * The menu in loop model
 */
export type MenuInLoopWritable = {
  /**
   * Serving Time
   */
  serving_time: string;
  /**
   * Looping Day Index
   */
  looping_day_index: number;
  menu: MenuWritable;
};

/**
 * MenuLoop
 *
 * The menu loop model
 */
export type MenuLoopWritable = {
  /**
   * Name
   */
  name: string;
  /**
   * Start Date
   */
  start_date: string;
  /**
   * End Date
   */
  end_date: string;
  /**
   * Days Per Cycle
   */
  days_per_cycle: number;
  /**
   * Id
   */
  id: number;
  /**
   * Menu Links
   */
  menu_links: Array<MenuInLoopWritable>;
  /**
   * Skip Dates
   */
  skip_dates?: Array<MenuLoopSpecialDateBasic>;
  /**
   * User Groups
   */
  user_groups?: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItemWritable;
  } | null;
};

/**
 * MenuPreview
 *
 * The future menu from a menu loop use for preview
 */
export type MenuPreviewWritable = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Serving Datetime
   */
  serving_datetime?: string | null;
  /**
   * Publish Prior To Serving Time
   */
  publish_prior_to_serving_time: string;
  /**
   * Cap Finalization Prior To Serving Time
   */
  cap_finalization_prior_to_serving_time: string;
  /**
   * Id
   */
  id: number;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Sections
   */
  sections: Array<MenuOptionGroupBasicWithItems>;
  /**
   * User Groups
   */
  user_groups: Array<UserGroupKeyWithSiteProfile>;
  /**
   * Involved Foods
   */
  involved_foods?: {
    [key: string]: FoodItemWritable;
  } | null;
  menu_loop: MenuLoopBasic;
};

/**
 * PasswordResetConfirm
 *
 * Body of the password-reset confirm endpoint
 */
export type PasswordResetConfirmWritable = {
  /**
   * Token
   */
  token: string;
  /**
   * New Password
   */
  new_password: string;
};

/**
 * PurchasingGuide
 *
 * The food item purchasing guide
 */
export type PurchasingGuideWritable = {
  /**
   * Data
   */
  data: Array<PurchasingGuideItem | PurchasingGuideItemAndFoodItemWritable>;
  /**
   * Warnings
   */
  warnings?: string | null;
};

/**
 * PurchasingGuideItemAndFoodItem
 *
 * The food item purchasing guiding item with all food item details
 */
export type PurchasingGuideItemAndFoodItemWritable = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Main Food Id
   */
  main_food_id?: number | null;
  /**
   * Supplier Product Code
   */
  supplier_product_code?: string | null;
  /**
   * Food Name
   */
  food_name?: string | null;
  /**
   * Required Amount
   */
  required_amount: number | number;
  /**
   * Reserved Amount
   */
  reserved_amount: number | number;
  /**
   * Stock Level
   */
  stock_level: number | number;
  /**
   * To Buy Amount
   */
  to_buy_amount: number | number;
  /**
   * To Buy Boxes
   */
  to_buy_boxes?: number | null;
  /**
   * Usages
   */
  usages?: Array<PurchasingGuideUsage>;
  food_item?: FoodItemWritable | null;
};

/**
 * StockReplenishmentCreateWithFoodItem
 *
 * Model of replenishment use at creation plus the food item data attached
 */
export type StockReplenishmentCreateWithFoodItemWritable = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Supplier Record Id
   */
  supplier_record_id: number;
  /**
   * Shelf Life
   */
  shelf_life: string;
  /**
   * Total Price
   */
  total_price: number;
  food_item: FoodItemWritable;
};

/**
 * SupplyProfileResponse
 *
 * Procurement profile info returned by the API.
 */
export type SupplyProfileResponseWritable = {
  /**
   * Name
   */
  name: string;
  /**
   * Jasper Site Id
   */
  jasper_site_id: number;
  supplier_id?: SupplierId | null;
  /**
   * Menu Cycle Weeks
   */
  menu_cycle_weeks?: number;
  /**
   * Timezone
   */
  timezone?: string;
  /**
   * Id
   */
  id: number;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Order Slots
   */
  order_slots: Array<ProcurementSlot>;
  /**
   * Order Email Recipients
   */
  order_email_recipients: Array<string>;
  /**
   * Ingredients
   */
  ingredients?: Array<SupplyProfileTrackedIngredientWritable>;
};

/**
 * SupplyProfileTrackedIngredient
 *
 * Output shape for a single tracked ingredient on a supply profile.
 *
 * Carries the link row's ``food_id`` and ``updated_at`` plus the
 * linked food's ``food_name`` (resolved via the ORM relationship and
 * exposed as a computed_field). Designed to grow as more per-link
 * metadata is surfaced without changing the top-level response shape.
 */
export type SupplyProfileTrackedIngredientWritable = {
  /**
   * Food Id
   */
  food_id: number;
  /**
   * Updated At
   */
  updated_at: string;
};

export type PostAuthTokenData = {
  body: BodyLoginForAccessTokenAuthTokenPostWritable;
  path?: never;
  query?: never;
  url: "/auth/token";
};

export type PostAuthTokenErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostAuthTokenError = PostAuthTokenErrors[keyof PostAuthTokenErrors];

export type PostAuthTokenResponses = {
  /**
   * Successful Response
   */
  200: Token;
};

export type PostAuthTokenResponse =
  PostAuthTokenResponses[keyof PostAuthTokenResponses];

export type PutAuthChangePasswordData = {
  body: BodyChangePasswordAuthChangePasswordPut;
  path?: never;
  query?: never;
  url: "/auth/change_password";
};

export type PutAuthChangePasswordErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PutAuthChangePasswordError =
  PutAuthChangePasswordErrors[keyof PutAuthChangePasswordErrors];

export type PutAuthChangePasswordResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostAuthPasswordResetRequestData = {
  body: PasswordResetRequest;
  path?: never;
  query?: never;
  url: "/auth/password_reset/request";
};

export type PostAuthPasswordResetRequestErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostAuthPasswordResetRequestError =
  PostAuthPasswordResetRequestErrors[keyof PostAuthPasswordResetRequestErrors];

export type PostAuthPasswordResetRequestResponses = {
  /**
   * Response Request Password Reset Auth Password Reset Request Post
   *
   * Successful Response
   */
  202: {
    [key: string]: unknown;
  };
};

export type PostAuthPasswordResetRequestResponse =
  PostAuthPasswordResetRequestResponses[keyof PostAuthPasswordResetRequestResponses];

export type PostAuthPasswordResetConfirmData = {
  body: PasswordResetConfirmWritable;
  path?: never;
  query?: never;
  url: "/auth/password_reset/confirm";
};

export type PostAuthPasswordResetConfirmErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostAuthPasswordResetConfirmError =
  PostAuthPasswordResetConfirmErrors[keyof PostAuthPasswordResetConfirmErrors];

export type PostAuthPasswordResetConfirmResponses = {
  /**
   * Successful Response
   */
  200: CredentialStatus;
};

export type PostAuthPasswordResetConfirmResponse =
  PostAuthPasswordResetConfirmResponses[keyof PostAuthPasswordResetConfirmResponses];

export type PostUserAccountData = {
  body: BodyCreateUserAccountUserAccountPost;
  path?: never;
  query?: never;
  url: "/user/account";
};

export type PostUserAccountErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserAccountError =
  PostUserAccountErrors[keyof PostUserAccountErrors];

export type PostUserAccountResponses = {
  /**
   * Successful Response
   */
  200: UserRead;
};

export type PostUserAccountResponse =
  PostUserAccountResponses[keyof PostUserAccountResponses];

export type GetUserAccountByUserIdData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/account/{user_id}";
};

export type GetUserAccountByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserAccountByUserIdError =
  GetUserAccountByUserIdErrors[keyof GetUserAccountByUserIdErrors];

export type GetUserAccountByUserIdResponses = {
  /**
   * Successful Response
   */
  200: UserRead;
};

export type GetUserAccountByUserIdResponse =
  GetUserAccountByUserIdResponses[keyof GetUserAccountByUserIdResponses];

export type PatchUserAccountByUserIdData = {
  body: UserUpdate;
  path: {
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/account/{user_id}";
};

export type PatchUserAccountByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchUserAccountByUserIdError =
  PatchUserAccountByUserIdErrors[keyof PatchUserAccountByUserIdErrors];

export type PatchUserAccountByUserIdResponses = {
  /**
   * Successful Response
   */
  200: UserRead;
};

export type PatchUserAccountByUserIdResponse =
  PatchUserAccountByUserIdResponses[keyof PatchUserAccountByUserIdResponses];

export type PostUserGroupData = {
  body: UserGroupCreate;
  path?: never;
  query?: never;
  url: "/user/group";
};

export type PostUserGroupErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserGroupError = PostUserGroupErrors[keyof PostUserGroupErrors];

export type PostUserGroupResponses = {
  /**
   * Successful Response
   */
  200: UserGroup;
};

export type PostUserGroupResponse =
  PostUserGroupResponses[keyof PostUserGroupResponses];

export type DeleteUserGroupByGroupIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}";
};

export type DeleteUserGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserGroupByGroupIdError =
  DeleteUserGroupByGroupIdErrors[keyof DeleteUserGroupByGroupIdErrors];

export type DeleteUserGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: UserGroup;
};

export type DeleteUserGroupByGroupIdResponse =
  DeleteUserGroupByGroupIdResponses[keyof DeleteUserGroupByGroupIdResponses];

export type GetUserGroupByGroupIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}";
};

export type GetUserGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserGroupByGroupIdError =
  GetUserGroupByGroupIdErrors[keyof GetUserGroupByGroupIdErrors];

export type GetUserGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: UserGroup;
};

export type GetUserGroupByGroupIdResponse =
  GetUserGroupByGroupIdResponses[keyof GetUserGroupByGroupIdResponses];

export type PatchUserGroupByGroupIdData = {
  body: UserGroupUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}";
};

export type PatchUserGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchUserGroupByGroupIdError =
  PatchUserGroupByGroupIdErrors[keyof PatchUserGroupByGroupIdErrors];

export type PatchUserGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: UserGroup;
};

export type PatchUserGroupByGroupIdResponse =
  PatchUserGroupByGroupIdResponses[keyof PatchUserGroupByGroupIdResponses];

export type GetUserGroupSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/user/group/search";
};

export type GetUserGroupSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserGroupSearchError =
  GetUserGroupSearchErrors[keyof GetUserGroupSearchErrors];

export type GetUserGroupSearchResponses = {
  /**
   * Response Search User Groups User Group Search Get
   *
   * Successful Response
   */
  200: Array<UserGroup> | Array<UserGroupKey>;
};

export type GetUserGroupSearchResponse =
  GetUserGroupSearchResponses[keyof GetUserGroupSearchResponses];

export type PatchUserGroupByGroupIdProfileData = {
  body: SiteProfileUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}/profile";
};

export type PatchUserGroupByGroupIdProfileErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchUserGroupByGroupIdProfileError =
  PatchUserGroupByGroupIdProfileErrors[keyof PatchUserGroupByGroupIdProfileErrors];

export type PatchUserGroupByGroupIdProfileResponses = {
  /**
   * Successful Response
   */
  200: UserGroup;
};

export type PatchUserGroupByGroupIdProfileResponse =
  PatchUserGroupByGroupIdProfileResponses[keyof PatchUserGroupByGroupIdProfileResponses];

export type DeleteUserGroupByGroupIdMembersByUserIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}/members/{user_id}";
};

export type DeleteUserGroupByGroupIdMembersByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserGroupByGroupIdMembersByUserIdError =
  DeleteUserGroupByGroupIdMembersByUserIdErrors[keyof DeleteUserGroupByGroupIdMembersByUserIdErrors];

export type DeleteUserGroupByGroupIdMembersByUserIdResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostUserGroupByGroupIdMembersByUserIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}/members/{user_id}";
};

export type PostUserGroupByGroupIdMembersByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserGroupByGroupIdMembersByUserIdError =
  PostUserGroupByGroupIdMembersByUserIdErrors[keyof PostUserGroupByGroupIdMembersByUserIdErrors];

export type PostUserGroupByGroupIdMembersByUserIdResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostUserPermissionData = {
  body: PermissionCreate;
  path?: never;
  query?: never;
  url: "/user/permission";
};

export type PostUserPermissionErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserPermissionError =
  PostUserPermissionErrors[keyof PostUserPermissionErrors];

export type PostUserPermissionResponses = {
  /**
   * Successful Response
   */
  200: Permission;
};

export type PostUserPermissionResponse =
  PostUserPermissionResponses[keyof PostUserPermissionResponses];

export type DeleteUserPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
  };
  query?: never;
  url: "/user/permission/{permission_id}";
};

export type DeleteUserPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserPermissionByPermissionIdError =
  DeleteUserPermissionByPermissionIdErrors[keyof DeleteUserPermissionByPermissionIdErrors];

export type DeleteUserPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: Permission;
};

export type DeleteUserPermissionByPermissionIdResponse =
  DeleteUserPermissionByPermissionIdResponses[keyof DeleteUserPermissionByPermissionIdResponses];

export type GetUserPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
  };
  query?: never;
  url: "/user/permission/{permission_id}";
};

export type GetUserPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserPermissionByPermissionIdError =
  GetUserPermissionByPermissionIdErrors[keyof GetUserPermissionByPermissionIdErrors];

export type GetUserPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: Permission;
};

export type GetUserPermissionByPermissionIdResponse =
  GetUserPermissionByPermissionIdResponses[keyof GetUserPermissionByPermissionIdResponses];

export type PatchUserPermissionByPermissionIdData = {
  body: PermissionUpdate;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
  };
  query?: never;
  url: "/user/permission/{permission_id}";
};

export type PatchUserPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchUserPermissionByPermissionIdError =
  PatchUserPermissionByPermissionIdErrors[keyof PatchUserPermissionByPermissionIdErrors];

export type PatchUserPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: Permission;
};

export type PatchUserPermissionByPermissionIdResponse =
  PatchUserPermissionByPermissionIdResponses[keyof PatchUserPermissionByPermissionIdResponses];

export type DeleteUserAccountByUserIdPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/account/{user_id}/permission/{permission_id}";
};

export type DeleteUserAccountByUserIdPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserAccountByUserIdPermissionByPermissionIdError =
  DeleteUserAccountByUserIdPermissionByPermissionIdErrors[keyof DeleteUserAccountByUserIdPermissionByPermissionIdErrors];

export type DeleteUserAccountByUserIdPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: UserPermissionDb;
};

export type DeleteUserAccountByUserIdPermissionByPermissionIdResponse =
  DeleteUserAccountByUserIdPermissionByPermissionIdResponses[keyof DeleteUserAccountByUserIdPermissionByPermissionIdResponses];

export type PostUserAccountByUserIdPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/user/account/{user_id}/permission/{permission_id}";
};

export type PostUserAccountByUserIdPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserAccountByUserIdPermissionByPermissionIdError =
  PostUserAccountByUserIdPermissionByPermissionIdErrors[keyof PostUserAccountByUserIdPermissionByPermissionIdErrors];

export type PostUserAccountByUserIdPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: UserPermissionDb;
};

export type PostUserAccountByUserIdPermissionByPermissionIdResponse =
  PostUserAccountByUserIdPermissionByPermissionIdResponses[keyof PostUserAccountByUserIdPermissionByPermissionIdResponses];

export type DeleteUserGroupByGroupIdPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}/permission/{permission_id}";
};

export type DeleteUserGroupByGroupIdPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserGroupByGroupIdPermissionByPermissionIdError =
  DeleteUserGroupByGroupIdPermissionByPermissionIdErrors[keyof DeleteUserGroupByGroupIdPermissionByPermissionIdErrors];

export type DeleteUserGroupByGroupIdPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: UserGroupPermissionDb;
};

export type DeleteUserGroupByGroupIdPermissionByPermissionIdResponse =
  DeleteUserGroupByGroupIdPermissionByPermissionIdResponses[keyof DeleteUserGroupByGroupIdPermissionByPermissionIdResponses];

export type PostUserGroupByGroupIdPermissionByPermissionIdData = {
  body?: never;
  path: {
    /**
     * Permission Id
     */
    permission_id: number;
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/user/group/{group_id}/permission/{permission_id}";
};

export type PostUserGroupByGroupIdPermissionByPermissionIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostUserGroupByGroupIdPermissionByPermissionIdError =
  PostUserGroupByGroupIdPermissionByPermissionIdErrors[keyof PostUserGroupByGroupIdPermissionByPermissionIdErrors];

export type PostUserGroupByGroupIdPermissionByPermissionIdResponses = {
  /**
   * Successful Response
   */
  200: UserGroupPermissionDb;
};

export type PostUserGroupByGroupIdPermissionByPermissionIdResponse =
  PostUserGroupByGroupIdPermissionByPermissionIdResponses[keyof PostUserGroupByGroupIdPermissionByPermissionIdResponses];

export type GetDietProfileInGroupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Group Ids
     */
    group_ids?: Array<number>;
  };
  url: "/diet_profile/in_group";
};

export type GetDietProfileInGroupErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDietProfileInGroupError =
  GetDietProfileInGroupErrors[keyof GetDietProfileInGroupErrors];

export type GetDietProfileInGroupResponses = {
  /**
   * Response Get All User Diet Profile In Group Diet Profile In Group Get
   *
   * Successful Response
   */
  200: Array<UserDietProfile>;
};

export type GetDietProfileInGroupResponse =
  GetDietProfileInGroupResponses[keyof GetDietProfileInGroupResponses];

export type GetDietProfileByUserIdData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/diet_profile/{user_id}";
};

export type GetDietProfileByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDietProfileByUserIdError =
  GetDietProfileByUserIdErrors[keyof GetDietProfileByUserIdErrors];

export type GetDietProfileByUserIdResponses = {
  /**
   * Successful Response
   */
  200: UserDietProfile;
};

export type GetDietProfileByUserIdResponse =
  GetDietProfileByUserIdResponses[keyof GetDietProfileByUserIdResponses];

export type PatchDietProfileByUserIdData = {
  body: BodyUpdateUserDietProfileDietProfileUserIdPatch;
  path: {
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/diet_profile/{user_id}";
};

export type PatchDietProfileByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchDietProfileByUserIdError =
  PatchDietProfileByUserIdErrors[keyof PatchDietProfileByUserIdErrors];

export type PatchDietProfileByUserIdResponses = {
  /**
   * Successful Response
   */
  200: UserDietProfile;
};

export type PatchDietProfileByUserIdResponse =
  PatchDietProfileByUserIdResponses[keyof PatchDietProfileByUserIdResponses];

export type PostDietProfileByUserIdData = {
  body: UserDietProfileCreateDeep;
  path: {
    /**
     * User Id
     */
    user_id: number;
  };
  query?: never;
  url: "/diet_profile/{user_id}";
};

export type PostDietProfileByUserIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostDietProfileByUserIdError =
  PostDietProfileByUserIdErrors[keyof PostDietProfileByUserIdErrors];

export type PostDietProfileByUserIdResponses = {
  /**
   * Successful Response
   */
  200: UserDietProfile;
};

export type PostDietProfileByUserIdResponse =
  PostDietProfileByUserIdResponses[keyof PostDietProfileByUserIdResponses];

export type PostFoodTagData = {
  body: BodyCreateFoodTagFoodTagPost;
  path?: never;
  query?: {
    /**
     * Tag Categories To Update
     */
    tag_categories_to_update?: Array<FoodTagCategory>;
  };
  url: "/food/tag";
};

export type PostFoodTagErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodTagError = PostFoodTagErrors[keyof PostFoodTagErrors];

export type PostFoodTagResponses = {
  /**
   * Successful Response
   */
  200: FoodTagWithTaggedFoodIds;
};

export type PostFoodTagResponse =
  PostFoodTagResponses[keyof PostFoodTagResponses];

export type PostFoodTagByTagIdApplyData = {
  /**
   * Additional Keywords
   */
  body?: Array<string> | null;
  path: {
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: {
    /**
     * Tag Categories To Update
     */
    tag_categories_to_update?: Array<FoodTagCategory>;
  };
  url: "/food/tag/{tag_id}/apply";
};

export type PostFoodTagByTagIdApplyErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodTagByTagIdApplyError =
  PostFoodTagByTagIdApplyErrors[keyof PostFoodTagByTagIdApplyErrors];

export type PostFoodTagByTagIdApplyResponses = {
  /**
   * Successful Response
   */
  200: FoodTagWithTaggedFoodIds;
};

export type PostFoodTagByTagIdApplyResponse =
  PostFoodTagByTagIdApplyResponses[keyof PostFoodTagByTagIdApplyResponses];

export type DeleteFoodTagByTagIdData = {
  body?: never;
  path: {
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: never;
  url: "/food/tag/{tag_id}";
};

export type DeleteFoodTagByTagIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteFoodTagByTagIdError =
  DeleteFoodTagByTagIdErrors[keyof DeleteFoodTagByTagIdErrors];

export type DeleteFoodTagByTagIdResponses = {
  /**
   * Successful Response
   */
  200: FoodTag;
};

export type DeleteFoodTagByTagIdResponse =
  DeleteFoodTagByTagIdResponses[keyof DeleteFoodTagByTagIdResponses];

export type GetFoodTagByTagIdData = {
  body?: never;
  path: {
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: never;
  url: "/food/tag/{tag_id}";
};

export type GetFoodTagByTagIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodTagByTagIdError =
  GetFoodTagByTagIdErrors[keyof GetFoodTagByTagIdErrors];

export type GetFoodTagByTagIdResponses = {
  /**
   * Successful Response
   */
  200: FoodTag;
};

export type GetFoodTagByTagIdResponse =
  GetFoodTagByTagIdResponses[keyof GetFoodTagByTagIdResponses];

export type PatchFoodTagByTagIdData = {
  body: FoodTagUpdate;
  path: {
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: never;
  url: "/food/tag/{tag_id}";
};

export type PatchFoodTagByTagIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchFoodTagByTagIdError =
  PatchFoodTagByTagIdErrors[keyof PatchFoodTagByTagIdErrors];

export type PatchFoodTagByTagIdResponses = {
  /**
   * Successful Response
   */
  200: FoodTag;
};

export type PatchFoodTagByTagIdResponse =
  PatchFoodTagByTagIdResponses[keyof PatchFoodTagByTagIdResponses];

export type GetFoodTagAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/food/tag/all";
};

export type GetFoodTagAllErrors = {
  /**
   * Not found
   */
  404: unknown;
};

export type GetFoodTagAllResponses = {
  /**
   * Response Get Food Tag All Food Tag All Get
   *
   * Successful Response
   */
  200: Array<FoodTag>;
};

export type GetFoodTagAllResponse =
  GetFoodTagAllResponses[keyof GetFoodTagAllResponses];

export type PostFoodItemApprovalByFoodIdData = {
  /**
   * Approving Roles
   */
  body?: Array<1 | 6 | 7> | null;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: {
    /**
     * Disapproval
     */
    disapproval?: boolean | null;
  };
  url: "/food/item/approval/{food_id}";
};

export type PostFoodItemApprovalByFoodIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodItemApprovalByFoodIdError =
  PostFoodItemApprovalByFoodIdErrors[keyof PostFoodItemApprovalByFoodIdErrors];

export type PostFoodItemApprovalByFoodIdResponses = {
  /**
   * Successful Response
   */
  200: FoodItem;
};

export type PostFoodItemApprovalByFoodIdResponse =
  PostFoodItemApprovalByFoodIdResponses[keyof PostFoodItemApprovalByFoodIdResponses];

export type PostFoodItemByFoodIdSwapUsagesData = {
  body: FoodItemSwapUsagesRequest;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: never;
  url: "/food/item/{food_id}/swap_usages";
};

export type PostFoodItemByFoodIdSwapUsagesErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodItemByFoodIdSwapUsagesError =
  PostFoodItemByFoodIdSwapUsagesErrors[keyof PostFoodItemByFoodIdSwapUsagesErrors];

export type PostFoodItemByFoodIdSwapUsagesResponses = {
  /**
   * Successful Response
   */
  200: FoodItemSwapUsagesReport;
};

export type PostFoodItemByFoodIdSwapUsagesResponse =
  PostFoodItemByFoodIdSwapUsagesResponses[keyof PostFoodItemByFoodIdSwapUsagesResponses];

export type GetFoodItemByFoodIdData = {
  body?: never;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: {
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
    return_type?: FoodItemReturnType;
  };
  url: "/food/item/{food_id}";
};

export type GetFoodItemByFoodIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodItemByFoodIdError =
  GetFoodItemByFoodIdErrors[keyof GetFoodItemByFoodIdErrors];

export type GetFoodItemByFoodIdResponses = {
  /**
   * Response Get Food Item Food Item  Food Id  Get
   *
   * Successful Response
   */
  200:
    | FoodItemAndRelatedFoods
    | FoodItemAndEquivalentFoods
    | FoodItemAndVariantFoods
    | FoodItemAndUsages
    | FoodItem
    | FoodItemKey;
};

export type GetFoodItemByFoodIdResponse =
  GetFoodItemByFoodIdResponses[keyof GetFoodItemByFoodIdResponses];

export type PostFoodItemByFoodIdTagsData = {
  /**
   * Body
   */
  body?: FoodItemEffectiveTagsRequest | null;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: {
    /**
     * Equivalent Selection
     */
    equivalent_selection?: string | null;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
  };
  url: "/food/item/{food_id}/tags";
};

export type PostFoodItemByFoodIdTagsErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodItemByFoodIdTagsError =
  PostFoodItemByFoodIdTagsErrors[keyof PostFoodItemByFoodIdTagsErrors];

export type PostFoodItemByFoodIdTagsResponses = {
  /**
   * Successful Response
   */
  200: FoodItemEffectiveTags;
};

export type PostFoodItemByFoodIdTagsResponse =
  PostFoodItemByFoodIdTagsResponses[keyof PostFoodItemByFoodIdTagsResponses];

export type GetFoodItemsData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Food Ids
     */
    food_ids: Array<number>;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
    return_type?: FoodItemReturnType;
  };
  url: "/food/items";
};

export type GetFoodItemsErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodItemsError = GetFoodItemsErrors[keyof GetFoodItemsErrors];

export type GetFoodItemsResponses = {
  /**
   * Response Get List Of Food Items Food Items Get
   *
   * Successful Response
   */
  200:
    | Array<FoodItemAndRelatedFoods>
    | Array<FoodItemAndEquivalentFoods>
    | Array<FoodItemAndVariantFoods>
    | Array<FoodItemAndUsages>
    | Array<FoodItem>
    | Array<FoodItemKey>;
};

export type GetFoodItemsResponse =
  GetFoodItemsResponses[keyof GetFoodItemsResponses];

export type GetFoodItemsDownloadData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Food Ids
     */
    food_ids?: Array<number>;
  };
  url: "/food/items/download";
};

export type GetFoodItemsDownloadErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodItemsDownloadError =
  GetFoodItemsDownloadErrors[keyof GetFoodItemsDownloadErrors];

export type GetFoodItemsDownloadResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetFoodItemSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
    /**
     * Only Procured Items
     */
    only_procured_items?: boolean | null;
    /**
     * Only Updated After
     */
    only_updated_after?: string | null;
    return_type?: FoodItemReturnType;
  };
  url: "/food/item/search";
};

export type GetFoodItemSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodItemSearchError =
  GetFoodItemSearchErrors[keyof GetFoodItemSearchErrors];

export type GetFoodItemSearchResponses = {
  /**
   * Response Search Food Items Food Item Search Get
   *
   * Successful Response
   */
  200:
    | PaginatedSearchResultsFoodItemAndRelatedFoods
    | PaginatedSearchResultsFoodItem
    | PaginatedSearchResultsFoodItemKey;
};

export type GetFoodItemSearchResponse =
  GetFoodItemSearchResponses[keyof GetFoodItemSearchResponses];

export type DeleteFoodItemByFoodIdTagByTagIdData = {
  body?: never;
  path: {
    /**
     * Food Id
     */
    food_id: number;
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: never;
  url: "/food/item/{food_id}/tag/{tag_id}";
};

export type DeleteFoodItemByFoodIdTagByTagIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteFoodItemByFoodIdTagByTagIdError =
  DeleteFoodItemByFoodIdTagByTagIdErrors[keyof DeleteFoodItemByFoodIdTagByTagIdErrors];

export type DeleteFoodItemByFoodIdTagByTagIdResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostFoodItemByFoodIdTagByTagIdData = {
  body?: never;
  path: {
    /**
     * Food Id
     */
    food_id: number;
    /**
     * Tag Id
     */
    tag_id: number;
  };
  query?: never;
  url: "/food/item/{food_id}/tag/{tag_id}";
};

export type PostFoodItemByFoodIdTagByTagIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodItemByFoodIdTagByTagIdError =
  PostFoodItemByFoodIdTagByTagIdErrors[keyof PostFoodItemByFoodIdTagByTagIdErrors];

export type PostFoodItemByFoodIdTagByTagIdResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PatchFoodItemsTagsData = {
  body: FoodItemTagsBatchEditRequest;
  path?: never;
  query?: never;
  url: "/food/items/tags";
};

export type PatchFoodItemsTagsErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchFoodItemsTagsError =
  PatchFoodItemsTagsErrors[keyof PatchFoodItemsTagsErrors];

export type PatchFoodItemsTagsResponses = {
  /**
   * Successful Response
   */
  200: FoodItemTagsBatchEditResponse;
};

export type PatchFoodItemsTagsResponse =
  PatchFoodItemsTagsResponses[keyof PatchFoodItemsTagsResponses];

export type GetFoodGroupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Group Type
     */
    group_type?: FoodGroupType | null;
    /**
     * Group Name Contains
     */
    group_name_contains?: string | null;
  };
  url: "/food/group";
};

export type GetFoodGroupErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodGroupError = GetFoodGroupErrors[keyof GetFoodGroupErrors];

export type GetFoodGroupResponses = {
  /**
   * Response Get List Of Food Groups Food Group Get
   *
   * Successful Response
   */
  200: Array<FoodGroup>;
};

export type GetFoodGroupResponse =
  GetFoodGroupResponses[keyof GetFoodGroupResponses];

export type PostFoodGroupData = {
  body: FoodGroupCreate;
  path?: never;
  query?: never;
  url: "/food/group";
};

export type PostFoodGroupErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodGroupError = PostFoodGroupErrors[keyof PostFoodGroupErrors];

export type PostFoodGroupResponses = {
  /**
   * Successful Response
   */
  200: FoodGroup;
};

export type PostFoodGroupResponse =
  PostFoodGroupResponses[keyof PostFoodGroupResponses];

export type DeleteFoodGroupByGroupIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/food/group/{group_id}";
};

export type DeleteFoodGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteFoodGroupByGroupIdError =
  DeleteFoodGroupByGroupIdErrors[keyof DeleteFoodGroupByGroupIdErrors];

export type DeleteFoodGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: FoodGroup;
};

export type DeleteFoodGroupByGroupIdResponse =
  DeleteFoodGroupByGroupIdResponses[keyof DeleteFoodGroupByGroupIdResponses];

export type GetFoodGroupByGroupIdData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/food/group/{group_id}";
};

export type GetFoodGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodGroupByGroupIdError =
  GetFoodGroupByGroupIdErrors[keyof GetFoodGroupByGroupIdErrors];

export type GetFoodGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: FoodGroup;
};

export type GetFoodGroupByGroupIdResponse =
  GetFoodGroupByGroupIdResponses[keyof GetFoodGroupByGroupIdResponses];

export type PatchFoodGroupByGroupIdData = {
  body: FoodGroupUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/food/group/{group_id}";
};

export type PatchFoodGroupByGroupIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchFoodGroupByGroupIdError =
  PatchFoodGroupByGroupIdErrors[keyof PatchFoodGroupByGroupIdErrors];

export type PatchFoodGroupByGroupIdResponses = {
  /**
   * Successful Response
   */
  200: FoodGroup;
};

export type PatchFoodGroupByGroupIdResponse =
  PatchFoodGroupByGroupIdResponses[keyof PatchFoodGroupByGroupIdResponses];

export type PutFoodGroupByGroupIdMembersData = {
  body: FoodGroupMembersSyncRequest;
  path: {
    /**
     * Group Id
     */
    group_id: number;
  };
  query?: never;
  url: "/food/group/{group_id}/members";
};

export type PutFoodGroupByGroupIdMembersErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PutFoodGroupByGroupIdMembersError =
  PutFoodGroupByGroupIdMembersErrors[keyof PutFoodGroupByGroupIdMembersErrors];

export type PutFoodGroupByGroupIdMembersResponses = {
  /**
   * Successful Response
   */
  200: FoodGroup;
};

export type PutFoodGroupByGroupIdMembersResponse =
  PutFoodGroupByGroupIdMembersResponses[keyof PutFoodGroupByGroupIdMembersResponses];

export type PostFoodIngData = {
  body: BodyCreateIngredientFoodIngPost;
  path?: never;
  query?: never;
  url: "/food/ing";
};

export type PostFoodIngErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodIngError = PostFoodIngErrors[keyof PostFoodIngErrors];

export type PostFoodIngResponses = {
  /**
   * Successful Response
   */
  200: FoodItemAndEquivalentFoods;
};

export type PostFoodIngResponse =
  PostFoodIngResponses[keyof PostFoodIngResponses];

export type PatchFoodIngByFoodIdData = {
  body?: BodyUpdateIngredientFoodIngFoodIdPatch;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: never;
  url: "/food/ing/{food_id}";
};

export type PatchFoodIngByFoodIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchFoodIngByFoodIdError =
  PatchFoodIngByFoodIdErrors[keyof PatchFoodIngByFoodIdErrors];

export type PatchFoodIngByFoodIdResponses = {
  /**
   * Successful Response
   */
  200: FoodItemAndEquivalentFoods;
};

export type PatchFoodIngByFoodIdResponse =
  PatchFoodIngByFoodIdResponses[keyof PatchFoodIngByFoodIdResponses];

export type PostFoodIngGfsScrapperCsvData = {
  body: BodyAutoTaggingFoodIngGfsScrapperCsvPost;
  path?: never;
  query?: {
    /**
     * Supplier Name
     */
    supplier_name?: string;
    /**
     * Dry Run
     */
    dry_run?: boolean | null;
  };
  url: "/food/ing/gfs_scrapper_csv";
};

export type PostFoodIngGfsScrapperCsvErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodIngGfsScrapperCsvError =
  PostFoodIngGfsScrapperCsvErrors[keyof PostFoodIngGfsScrapperCsvErrors];

export type PostFoodIngGfsScrapperCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
  /**
   * Accepted
   */
  202: Blob | File;
};

export type PostFoodIngGfsScrapperCsvResponse =
  PostFoodIngGfsScrapperCsvResponses[keyof PostFoodIngGfsScrapperCsvResponses];

export type PostFoodSupplierInfoInvoiceData = {
  body: BodyInvoiceBatchUpdateSupplierInfoFoodSupplierInfoInvoicePost;
  path?: never;
  query?: {
    /**
     * Supplier Name
     */
    supplier_name?: string;
  };
  url: "/food/supplier_info/invoice";
};

export type PostFoodSupplierInfoInvoiceErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodSupplierInfoInvoiceError =
  PostFoodSupplierInfoInvoiceErrors[keyof PostFoodSupplierInfoInvoiceErrors];

export type PostFoodSupplierInfoInvoiceResponses = {
  /**
   * Response Invoice Batch Update Supplier Info Food Supplier Info Invoice Post
   *
   * Successful Response
   */
  200: [
    Array<GordonFoodServiceInvoiceRecord>,
    Array<GordonFoodServiceInvoiceRecord>,
  ];
};

export type PostFoodSupplierInfoInvoiceResponse =
  PostFoodSupplierInfoInvoiceResponses[keyof PostFoodSupplierInfoInvoiceResponses];

export type GetFoodSupplierInfoLookupData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Product Code
     */
    product_code: string;
    /**
     * Exclude Food Id
     */
    exclude_food_id?: number | null;
  };
  url: "/food/supplier_info/lookup";
};

export type GetFoodSupplierInfoLookupErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodSupplierInfoLookupError =
  GetFoodSupplierInfoLookupErrors[keyof GetFoodSupplierInfoLookupErrors];

export type GetFoodSupplierInfoLookupResponses = {
  /**
   * Response Lookup Supplier Info By Product Code Food Supplier Info Lookup Get
   *
   * Successful Response
   */
  200: Array<FoodItemKey>;
};

export type GetFoodSupplierInfoLookupResponse =
  GetFoodSupplierInfoLookupResponses[keyof GetFoodSupplierInfoLookupResponses];

export type GetFoodRecipeSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/food/recipe/search";
};

export type GetFoodRecipeSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFoodRecipeSearchError =
  GetFoodRecipeSearchErrors[keyof GetFoodRecipeSearchErrors];

export type GetFoodRecipeSearchResponses = {
  /**
   * Response Search Food Recipes Food Recipe Search Get
   *
   * Successful Response
   */
  200: PaginatedSearchResultsFoodRecipe | PaginatedSearchResultsFoodRecipeKey;
};

export type GetFoodRecipeSearchResponse =
  GetFoodRecipeSearchResponses[keyof GetFoodRecipeSearchResponses];

export type PostFoodRecipeSearchData = {
  /**
   * Params
   */
  body?: FoodItemSearchingParams | null;
  path?: never;
  query?: {
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/food/recipe/search";
};

export type PostFoodRecipeSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodRecipeSearchError =
  PostFoodRecipeSearchErrors[keyof PostFoodRecipeSearchErrors];

export type PostFoodRecipeSearchResponses = {
  /**
   * Response Advance Search Food Recipes Food Recipe Search Post
   *
   * Successful Response
   */
  200: PaginatedSearchResultsFoodRecipe | PaginatedSearchResultsFoodRecipeKey;
};

export type PostFoodRecipeSearchResponse =
  PostFoodRecipeSearchResponses[keyof PostFoodRecipeSearchResponses];

export type PostFoodRecipeData = {
  body: BodyDeepCreateRecipeFoodRecipePost;
  path?: never;
  query?: never;
  url: "/food/recipe";
};

export type PostFoodRecipeErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostFoodRecipeError =
  PostFoodRecipeErrors[keyof PostFoodRecipeErrors];

export type PostFoodRecipeResponses = {
  /**
   * Successful Response
   */
  200: FoodItemAndVariantFoods;
};

export type PostFoodRecipeResponse =
  PostFoodRecipeResponses[keyof PostFoodRecipeResponses];

export type PatchFoodItemByFoodIdRecipeByRecipeIdData = {
  body?: BodyDeepUpdateRecipeFoodItemFoodIdRecipeRecipeIdPatch;
  path: {
    /**
     * Food Id
     */
    food_id: number;
    /**
     * Recipe Id
     */
    recipe_id: number;
  };
  query?: never;
  url: "/food/item/{food_id}/recipe/{recipe_id}";
};

export type PatchFoodItemByFoodIdRecipeByRecipeIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchFoodItemByFoodIdRecipeByRecipeIdError =
  PatchFoodItemByFoodIdRecipeByRecipeIdErrors[keyof PatchFoodItemByFoodIdRecipeByRecipeIdErrors];

export type PatchFoodItemByFoodIdRecipeByRecipeIdResponses = {
  /**
   * Successful Response
   */
  200: FoodItemAndVariantFoods;
};

export type PatchFoodItemByFoodIdRecipeByRecipeIdResponse =
  PatchFoodItemByFoodIdRecipeByRecipeIdResponses[keyof PatchFoodItemByFoodIdRecipeByRecipeIdResponses];

export type GetMenuDailyByQueryDateDownloadData = {
  body?: never;
  path: {
    /**
     * Query Date
     */
    query_date: string;
  };
  query?: never;
  url: "/menu/daily/{query_date}/download";
};

export type GetMenuDailyByQueryDateDownloadErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuDailyByQueryDateDownloadError =
  GetMenuDailyByQueryDateDownloadErrors[keyof GetMenuDailyByQueryDateDownloadErrors];

export type GetMenuDailyByQueryDateDownloadResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetMenuDailyByQueryDateData = {
  body?: never;
  path: {
    /**
     * Query Date
     */
    query_date: string;
  };
  query?: {
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: string | null;
    /**
     * Aggregate Food Tags
     */
    aggregate_food_tags?: boolean;
    /**
     * Fill Item Display Names
     */
    fill_item_display_names?: boolean;
    /**
     * Fill Latest Food Costs
     */
    fill_latest_food_costs?: boolean;
    /**
     * Fill Involved Foods
     */
    fill_involved_foods?: boolean;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
    /**
     * Menu Type
     */
    menu_type?: "cycling menu" | "alternate menu" | null;
  };
  url: "/menu/daily/{query_date}";
};

export type GetMenuDailyByQueryDateErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuDailyByQueryDateError =
  GetMenuDailyByQueryDateErrors[keyof GetMenuDailyByQueryDateErrors];

export type GetMenuDailyByQueryDateResponses = {
  /**
   * Response Get Daily Menus Menu Daily  Query Date  Get
   *
   * Successful Response
   */
  200: Array<MenuPreview | Menu>;
};

export type GetMenuDailyByQueryDateResponse =
  GetMenuDailyByQueryDateResponses[keyof GetMenuDailyByQueryDateResponses];

export type GetMenuDateRangeFromByDateStartToByDateEndData = {
  body?: never;
  path: {
    /**
     * Date Start
     */
    date_start: string;
    /**
     * Date End
     */
    date_end: string;
  };
  query?: {
    /**
     * Tz Info Str
     */
    tz_info_str?: string | null;
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: string | null;
    /**
     * Aggregate Food Tags
     */
    aggregate_food_tags?: boolean;
    /**
     * Fill Item Display Names
     */
    fill_item_display_names?: boolean;
    /**
     * Fill Latest Food Costs
     */
    fill_latest_food_costs?: boolean;
    /**
     * Fill Involved Foods
     */
    fill_involved_foods?: boolean;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
    /**
     * Menu Type
     */
    menu_type?: "cycling menu" | "alternate menu" | null;
  };
  url: "/menu/date_range/from/{date_start}/to/{date_end}";
};

export type GetMenuDateRangeFromByDateStartToByDateEndErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuDateRangeFromByDateStartToByDateEndError =
  GetMenuDateRangeFromByDateStartToByDateEndErrors[keyof GetMenuDateRangeFromByDateStartToByDateEndErrors];

export type GetMenuDateRangeFromByDateStartToByDateEndResponses = {
  /**
   * Response Get Menus In Date Range Menu Date Range From  Date Start  To  Date End  Get
   *
   * Successful Response
   */
  200: Array<MenuPreview | Menu>;
};

export type GetMenuDateRangeFromByDateStartToByDateEndResponse =
  GetMenuDateRangeFromByDateStartToByDateEndResponses[keyof GetMenuDateRangeFromByDateStartToByDateEndResponses];

export type GetMenuMealSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/menu/meal/search";
};

export type GetMenuMealSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuMealSearchError =
  GetMenuMealSearchErrors[keyof GetMenuMealSearchErrors];

export type GetMenuMealSearchResponses = {
  /**
   * Response Search Menus Menu Meal Search Get
   *
   * Successful Response
   */
  200: PaginatedSearchResultsMenu | PaginatedSearchResultsMenuBasic;
};

export type GetMenuMealSearchResponse =
  GetMenuMealSearchResponses[keyof GetMenuMealSearchResponses];

export type PostMenuMealSearchData = {
  /**
   * Params
   */
  body?: MenuSearchingParams | null;
  path?: never;
  query?: {
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/menu/meal/search";
};

export type PostMenuMealSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuMealSearchError =
  PostMenuMealSearchErrors[keyof PostMenuMealSearchErrors];

export type PostMenuMealSearchResponses = {
  /**
   * Response Advance Search Menus Menu Meal Search Post
   *
   * Successful Response
   */
  200: PaginatedSearchResultsMenu | PaginatedSearchResultsMenuBasic;
};

export type PostMenuMealSearchResponse =
  PostMenuMealSearchResponses[keyof PostMenuMealSearchResponses];

export type DeleteMenuMealByMenuIdData = {
  body?: never;
  path: {
    /**
     * Menu Id
     */
    menu_id: number;
  };
  query?: never;
  url: "/menu/meal/{menu_id}";
};

export type DeleteMenuMealByMenuIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteMenuMealByMenuIdError =
  DeleteMenuMealByMenuIdErrors[keyof DeleteMenuMealByMenuIdErrors];

export type DeleteMenuMealByMenuIdResponses = {
  /**
   * Successful Response
   */
  200: Menu;
};

export type DeleteMenuMealByMenuIdResponse =
  DeleteMenuMealByMenuIdResponses[keyof DeleteMenuMealByMenuIdResponses];

export type GetMenuMealByMenuIdData = {
  body?: never;
  path: {
    /**
     * Menu Id
     */
    menu_id: number;
  };
  query?: {
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * Equivalent Selection Site Id
     */
    equivalent_selection_site_id?: number | null;
    /**
     * Equivalent Selection
     */
    equivalent_selection?: string | null;
    /**
     * Aggregate Food Tags
     */
    aggregate_food_tags?: boolean;
    /**
     * Fill Item Display Names
     */
    fill_item_display_names?: boolean;
    /**
     * Fill Latest Food Costs
     */
    fill_latest_food_costs?: boolean;
    /**
     * Fill Involved Foods
     */
    fill_involved_foods?: boolean;
  };
  url: "/menu/meal/{menu_id}";
};

export type GetMenuMealByMenuIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuMealByMenuIdError =
  GetMenuMealByMenuIdErrors[keyof GetMenuMealByMenuIdErrors];

export type GetMenuMealByMenuIdResponses = {
  /**
   * Successful Response
   */
  200: Menu;
};

export type GetMenuMealByMenuIdResponse =
  GetMenuMealByMenuIdResponses[keyof GetMenuMealByMenuIdResponses];

export type PutMenuMealByMenuIdData = {
  body: MenuUpdateDeep;
  path: {
    /**
     * Menu Id
     */
    menu_id: number;
  };
  query?: never;
  url: "/menu/meal/{menu_id}";
};

export type PutMenuMealByMenuIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PutMenuMealByMenuIdError =
  PutMenuMealByMenuIdErrors[keyof PutMenuMealByMenuIdErrors];

export type PutMenuMealByMenuIdResponses = {
  /**
   * Successful Response
   */
  200: Menu;
};

export type PutMenuMealByMenuIdResponse =
  PutMenuMealByMenuIdResponses[keyof PutMenuMealByMenuIdResponses];

export type PostMenuMealData = {
  body: MenuCreateDeep;
  path?: never;
  query?: never;
  url: "/menu/meal";
};

export type PostMenuMealErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuMealError = PostMenuMealErrors[keyof PostMenuMealErrors];

export type PostMenuMealResponses = {
  /**
   * Successful Response
   */
  200: Menu;
};

export type PostMenuMealResponse =
  PostMenuMealResponses[keyof PostMenuMealResponses];

export type PatchMenuMealBatchData = {
  /**
   * Menus
   */
  body: Array<MenuUpdateDeepInBatch>;
  path?: never;
  query?: never;
  url: "/menu/meal/batch";
};

export type PatchMenuMealBatchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchMenuMealBatchError =
  PatchMenuMealBatchErrors[keyof PatchMenuMealBatchErrors];

export type PatchMenuMealBatchResponses = {
  /**
   * Response Deep Update Menus Menu Meal Batch Patch
   *
   * Successful Response
   */
  200: Array<Menu>;
};

export type PatchMenuMealBatchResponse =
  PatchMenuMealBatchResponses[keyof PatchMenuMealBatchResponses];

export type PostMenuMealBatchData = {
  /**
   * Menus
   */
  body: Array<MenuCreateDeep>;
  path?: never;
  query?: never;
  url: "/menu/meal/batch";
};

export type PostMenuMealBatchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuMealBatchError =
  PostMenuMealBatchErrors[keyof PostMenuMealBatchErrors];

export type PostMenuMealBatchResponses = {
  /**
   * Response Deep Create Menus Menu Meal Batch Post
   *
   * Successful Response
   */
  200: Array<Menu>;
};

export type PostMenuMealBatchResponse =
  PostMenuMealBatchResponses[keyof PostMenuMealBatchResponses];

export type GetMenuLoopSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/menu/loop/search";
};

export type GetMenuLoopSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuLoopSearchError =
  GetMenuLoopSearchErrors[keyof GetMenuLoopSearchErrors];

export type GetMenuLoopSearchResponses = {
  /**
   * Response Search Menu Loop Menu Loop Search Get
   *
   * Successful Response
   */
  200: PaginatedSearchResultsMenuLoop | PaginatedSearchResultsMenuLoopBasic;
};

export type GetMenuLoopSearchResponse =
  GetMenuLoopSearchResponses[keyof GetMenuLoopSearchResponses];

export type PostMenuLoopSearchData = {
  /**
   * Params
   */
  body?: MenuLoopSearchingParams | null;
  path?: never;
  query?: {
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Return Keys Only
     */
    return_keys_only?: boolean | null;
  };
  url: "/menu/loop/search";
};

export type PostMenuLoopSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuLoopSearchError =
  PostMenuLoopSearchErrors[keyof PostMenuLoopSearchErrors];

export type PostMenuLoopSearchResponses = {
  /**
   * Response Advance Search Menu Loop Menu Loop Search Post
   *
   * Successful Response
   */
  200: PaginatedSearchResultsMenuLoop | PaginatedSearchResultsMenuLoopBasic;
};

export type PostMenuLoopSearchResponse =
  PostMenuLoopSearchResponses[keyof PostMenuLoopSearchResponses];

export type DeleteMenuLoopByMenuLoopIdData = {
  body?: never;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: never;
  url: "/menu/loop/{menu_loop_id}";
};

export type DeleteMenuLoopByMenuLoopIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteMenuLoopByMenuLoopIdError =
  DeleteMenuLoopByMenuLoopIdErrors[keyof DeleteMenuLoopByMenuLoopIdErrors];

export type DeleteMenuLoopByMenuLoopIdResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type DeleteMenuLoopByMenuLoopIdResponse =
  DeleteMenuLoopByMenuLoopIdResponses[keyof DeleteMenuLoopByMenuLoopIdResponses];

export type GetMenuLoopByMenuLoopIdData = {
  body?: never;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: {
    /**
     * Fill Involved Foods
     */
    fill_involved_foods?: boolean;
  };
  url: "/menu/loop/{menu_loop_id}";
};

export type GetMenuLoopByMenuLoopIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuLoopByMenuLoopIdError =
  GetMenuLoopByMenuLoopIdErrors[keyof GetMenuLoopByMenuLoopIdErrors];

export type GetMenuLoopByMenuLoopIdResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type GetMenuLoopByMenuLoopIdResponse =
  GetMenuLoopByMenuLoopIdResponses[keyof GetMenuLoopByMenuLoopIdResponses];

export type PatchMenuLoopByMenuLoopIdData = {
  body: MenuLoopUpdateDeep;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: never;
  url: "/menu/loop/{menu_loop_id}";
};

export type PatchMenuLoopByMenuLoopIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchMenuLoopByMenuLoopIdError =
  PatchMenuLoopByMenuLoopIdErrors[keyof PatchMenuLoopByMenuLoopIdErrors];

export type PatchMenuLoopByMenuLoopIdResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type PatchMenuLoopByMenuLoopIdResponse =
  PatchMenuLoopByMenuLoopIdResponses[keyof PatchMenuLoopByMenuLoopIdResponses];

export type PostMenuLoopByMenuLoopIdData = {
  body: MenuLoopCopyParams;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: never;
  url: "/menu/loop/{menu_loop_id}";
};

export type PostMenuLoopByMenuLoopIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuLoopByMenuLoopIdError =
  PostMenuLoopByMenuLoopIdErrors[keyof PostMenuLoopByMenuLoopIdErrors];

export type PostMenuLoopByMenuLoopIdResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type PostMenuLoopByMenuLoopIdResponse =
  PostMenuLoopByMenuLoopIdResponses[keyof PostMenuLoopByMenuLoopIdResponses];

export type GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadData = {
  body?: never;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: never;
  url: "/menu/loop/{menu_loop_id}/involved_foods/download";
};

export type GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadError =
  GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadErrors[keyof GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadErrors];

export type GetMenuLoopByMenuLoopIdInvolvedFoodsDownloadResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostMenuLoopData = {
  body: MenuLoopCreateDeep;
  path?: never;
  query?: never;
  url: "/menu/loop";
};

export type PostMenuLoopErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuLoopError = PostMenuLoopErrors[keyof PostMenuLoopErrors];

export type PostMenuLoopResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type PostMenuLoopResponse =
  PostMenuLoopResponses[keyof PostMenuLoopResponses];

export type PostMenuLoopByMenuLoopIdSkipDateData = {
  /**
   * New Special Date Events
   */
  body: Array<MenuLoopSpecialDateCreate>;
  path: {
    /**
     * Menu Loop Id
     */
    menu_loop_id: number;
  };
  query?: never;
  url: "/menu/loop/{menu_loop_id}/skip_date";
};

export type PostMenuLoopByMenuLoopIdSkipDateErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostMenuLoopByMenuLoopIdSkipDateError =
  PostMenuLoopByMenuLoopIdSkipDateErrors[keyof PostMenuLoopByMenuLoopIdSkipDateErrors];

export type PostMenuLoopByMenuLoopIdSkipDateResponses = {
  /**
   * Successful Response
   */
  200: MenuLoop;
};

export type PostMenuLoopByMenuLoopIdSkipDateResponse =
  PostMenuLoopByMenuLoopIdSkipDateResponses[keyof PostMenuLoopByMenuLoopIdSkipDateResponses];

export type GetOrderTicketsDailyByQueryDateData = {
  body?: never;
  path: {
    /**
     * Query Date
     */
    query_date: string;
  };
  query?: {
    /**
     * Tz Info Str
     */
    tz_info_str?: string | null;
  };
  url: "/order/tickets/daily/{query_date}";
};

export type GetOrderTicketsDailyByQueryDateErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrderTicketsDailyByQueryDateError =
  GetOrderTicketsDailyByQueryDateErrors[keyof GetOrderTicketsDailyByQueryDateErrors];

export type GetOrderTicketsDailyByQueryDateResponses = {
  /**
   * Response Get Daily Meal Order Tickets Order Tickets Daily  Query Date  Get
   *
   * Successful Response
   */
  200: Array<MealOrderTicket>;
};

export type GetOrderTicketsDailyByQueryDateResponse =
  GetOrderTicketsDailyByQueryDateResponses[keyof GetOrderTicketsDailyByQueryDateResponses];

export type GetOrderMealByMealOrderTicketIdData = {
  body?: never;
  path: {
    /**
     * Meal Order Ticket Id
     */
    meal_order_ticket_id: number;
  };
  query?: never;
  url: "/order/meal/{meal_order_ticket_id}";
};

export type GetOrderMealByMealOrderTicketIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrderMealByMealOrderTicketIdError =
  GetOrderMealByMealOrderTicketIdErrors[keyof GetOrderMealByMealOrderTicketIdErrors];

export type GetOrderMealByMealOrderTicketIdResponses = {
  /**
   * Successful Response
   */
  200: MealOrderTicket;
};

export type GetOrderMealByMealOrderTicketIdResponse =
  GetOrderMealByMealOrderTicketIdResponses[keyof GetOrderMealByMealOrderTicketIdResponses];

export type PatchOrderMealByMealOrderTicketIdData = {
  body: MealOrderTicketUpdateDeep;
  path: {
    /**
     * Meal Order Ticket Id
     */
    meal_order_ticket_id: number;
  };
  query?: never;
  url: "/order/meal/{meal_order_ticket_id}";
};

export type PatchOrderMealByMealOrderTicketIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchOrderMealByMealOrderTicketIdError =
  PatchOrderMealByMealOrderTicketIdErrors[keyof PatchOrderMealByMealOrderTicketIdErrors];

export type PatchOrderMealByMealOrderTicketIdResponses = {
  /**
   * Successful Response
   */
  200: MealOrderTicket;
};

export type PatchOrderMealByMealOrderTicketIdResponse =
  PatchOrderMealByMealOrderTicketIdResponses[keyof PatchOrderMealByMealOrderTicketIdResponses];

export type PostOrderMealData = {
  body: MealOrderTicketCreateDeep;
  path?: never;
  query?: never;
  url: "/order/meal";
};

export type PostOrderMealErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostOrderMealError = PostOrderMealErrors[keyof PostOrderMealErrors];

export type PostOrderMealResponses = {
  /**
   * Successful Response
   */
  200: MealOrderTicket;
};

export type PostOrderMealResponse =
  PostOrderMealResponses[keyof PostOrderMealResponses];

export type GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndData =
  {
    body?: never;
    path: {
      /**
       * Date Start
       */
      date_start: string;
      /**
       * Date End
       */
      date_end: string;
    };
    query?: {
      /**
       * Tz Info Str
       */
      tz_info_str?: string | null;
    };
    url: "/order/portion/aggregated/menu_production/date/from/{date_start}/to/{date_end}";
  };

export type GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndErrors =
  {
    /**
     * Not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
  };

export type GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndError =
  GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndErrors[keyof GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndErrors];

export type GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndResponses =
  {
    /**
     * Response Get Item Production Numbers On Menus In Date Range Order Portion Aggregated Menu Production Date From  Date Start  To  Date End  Get
     *
     * Successful Response
     */
    200: Array<DishOrderPortions>;
  };

export type GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndResponse =
  GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndResponses[keyof GetOrderPortionAggregatedMenuProductionDateFromByDateStartToByDateEndResponses];

export type PostInventoryStockReplenishmentSupplierOrderCsvFileData = {
  body: BodyExtractReplenishmentFromSupplierOrderCsvInventoryStockReplenishmentSupplierOrderCsvFilePost;
  path?: never;
  query?: {
    /**
     * Supplier Name
     */
    supplier_name?: string;
  };
  url: "/inventory/stock/replenishment/supplier_order_csv_file";
};

export type PostInventoryStockReplenishmentSupplierOrderCsvFileErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryStockReplenishmentSupplierOrderCsvFileError =
  PostInventoryStockReplenishmentSupplierOrderCsvFileErrors[keyof PostInventoryStockReplenishmentSupplierOrderCsvFileErrors];

export type PostInventoryStockReplenishmentSupplierOrderCsvFileResponses = {
  /**
   * Response Extract Replenishment From Supplier Order Csv Inventory Stock Replenishment Supplier Order Csv File Post
   *
   * Successful Response
   */
  200: [
    Array<GordonFoodServiceOrderRecord>,
    Array<StockReplenishmentCreateWithFoodItem>,
  ];
};

export type PostInventoryStockReplenishmentSupplierOrderCsvFileResponse =
  PostInventoryStockReplenishmentSupplierOrderCsvFileResponses[keyof PostInventoryStockReplenishmentSupplierOrderCsvFileResponses];

export type PostInventoryStockReplenishmentData = {
  /**
   * Records
   */
  body: Array<StockReplenishmentCreate>;
  path?: never;
  query?: never;
  url: "/inventory/stock/replenishment";
};

export type PostInventoryStockReplenishmentErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryStockReplenishmentError =
  PostInventoryStockReplenishmentErrors[keyof PostInventoryStockReplenishmentErrors];

export type PostInventoryStockReplenishmentResponses = {
  /**
   * Response Replenish Stock Inventory Stock Replenishment Post
   *
   * Successful Response
   */
  200: Array<StockReplenishment>;
};

export type PostInventoryStockReplenishmentResponse =
  PostInventoryStockReplenishmentResponses[keyof PostInventoryStockReplenishmentResponses];

export type PostInventoryStockDepletionData = {
  /**
   * Depletion Amounts
   */
  body: Array<FoodQuantity>;
  path?: never;
  query?: never;
  url: "/inventory/stock/depletion";
};

export type PostInventoryStockDepletionErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryStockDepletionError =
  PostInventoryStockDepletionErrors[keyof PostInventoryStockDepletionErrors];

export type PostInventoryStockDepletionResponses = {
  /**
   * Response Deplete Stocks Inventory Stock Depletion Post
   *
   * Successful Response
   */
  200: Array<StockDepletion>;
};

export type PostInventoryStockDepletionResponse =
  PostInventoryStockDepletionResponses[keyof PostInventoryStockDepletionResponses];

export type GetInventoryStockByFoodIdDetailData = {
  body?: never;
  path: {
    /**
     * Food Id
     */
    food_id: number;
  };
  query?: never;
  url: "/inventory/stock/{food_id}/detail";
};

export type GetInventoryStockByFoodIdDetailErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryStockByFoodIdDetailError =
  GetInventoryStockByFoodIdDetailErrors[keyof GetInventoryStockByFoodIdDetailErrors];

export type GetInventoryStockByFoodIdDetailResponses = {
  /**
   * Response Get Stock Detail Of A Food Inventory Stock  Food Id  Detail Get
   *
   * Successful Response
   */
  200: Array<StockReplenishment>;
};

export type GetInventoryStockByFoodIdDetailResponse =
  GetInventoryStockByFoodIdDetailResponses[keyof GetInventoryStockByFoodIdDetailResponses];

export type GetInventoryStocksData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Food Ids
     */
    food_ids: Array<number>;
  };
  url: "/inventory/stocks";
};

export type GetInventoryStocksErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryStocksError =
  GetInventoryStocksErrors[keyof GetInventoryStocksErrors];

export type GetInventoryStocksResponses = {
  /**
   * Response Get Stock Level Of A List Of Food Items Inventory Stocks Get
   *
   * Successful Response
   */
  200: Array<FoodQuantity>;
};

export type GetInventoryStocksResponse =
  GetInventoryStocksResponses[keyof GetInventoryStocksResponses];

export type GetInventoryStocksSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Keywords
     */
    keywords?: string | null;
    /**
     * Number Per Page
     */
    number_per_page?: number | null;
    /**
     * Page Index
     */
    page_index?: number | null;
    /**
     * Only Procured Items
     */
    only_procured_items?: boolean | null;
  };
  url: "/inventory/stocks/search";
};

export type GetInventoryStocksSearchErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryStocksSearchError =
  GetInventoryStocksSearchErrors[keyof GetInventoryStocksSearchErrors];

export type GetInventoryStocksSearchResponses = {
  /**
   * Successful Response
   */
  200: PaginatedSearchResultsFoodQuantity;
};

export type GetInventoryStocksSearchResponse =
  GetInventoryStocksSearchResponses[keyof GetInventoryStocksSearchResponses];

export type PostInventoryPurchasingFromPortionOrdersData = {
  body: BodyGetPurchasingGuideFromPortionOrdersInventoryPurchasingFromPortionOrdersPost;
  path?: never;
  query?: {
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
  };
  url: "/inventory/purchasing/from_portion_orders";
};

export type PostInventoryPurchasingFromPortionOrdersErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryPurchasingFromPortionOrdersError =
  PostInventoryPurchasingFromPortionOrdersErrors[keyof PostInventoryPurchasingFromPortionOrdersErrors];

export type PostInventoryPurchasingFromPortionOrdersResponses = {
  /**
   * Successful Response
   */
  200: PurchasingGuide;
};

export type PostInventoryPurchasingFromPortionOrdersResponse =
  PostInventoryPurchasingFromPortionOrdersResponses[keyof PostInventoryPurchasingFromPortionOrdersResponses];

export type PostInventoryPurchasingFromCustomMenusOnDatesData = {
  body: BodyGetPurchasingGuideFromCustomMenusOnDatesInventoryPurchasingFromCustomMenusOnDatesPost;
  path?: never;
  query?: {
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
  };
  url: "/inventory/purchasing/from_custom_menus_on_dates";
};

export type PostInventoryPurchasingFromCustomMenusOnDatesErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryPurchasingFromCustomMenusOnDatesError =
  PostInventoryPurchasingFromCustomMenusOnDatesErrors[keyof PostInventoryPurchasingFromCustomMenusOnDatesErrors];

export type PostInventoryPurchasingFromCustomMenusOnDatesResponses = {
  /**
   * Successful Response
   */
  200: PurchasingGuide;
};

export type PostInventoryPurchasingFromCustomMenusOnDatesResponse =
  PostInventoryPurchasingFromCustomMenusOnDatesResponses[keyof PostInventoryPurchasingFromCustomMenusOnDatesResponses];

export type PostInventoryPurchasingFromDateRangeData = {
  body: BodyGetPurchasingGuideFromDateRangeInventoryPurchasingFromDateRangePost;
  path?: never;
  query?: {
    /**
     * Tz Info Str
     */
    tz_info_str?: string | null;
    /**
     * Menu Type
     */
    menu_type?: "cycling menu" | "alternate menu";
    /**
     * Auto Resolve Equivalent Selection
     */
    auto_resolve_equivalent_selection?: boolean;
    /**
     * For Site Id
     */
    for_site_id?: number | null;
  };
  url: "/inventory/purchasing/from_date_range";
};

export type PostInventoryPurchasingFromDateRangeErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryPurchasingFromDateRangeError =
  PostInventoryPurchasingFromDateRangeErrors[keyof PostInventoryPurchasingFromDateRangeErrors];

export type PostInventoryPurchasingFromDateRangeResponses = {
  /**
   * Successful Response
   */
  200: PurchasingGuide;
};

export type PostInventoryPurchasingFromDateRangeResponse =
  PostInventoryPurchasingFromDateRangeResponses[keyof PostInventoryPurchasingFromDateRangeResponses];

export type GetInventoryGfsOrderingFacilityData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/facility/";
};

export type GetInventoryGfsOrderingFacilityErrors = {
  /**
   * Not found
   */
  404: unknown;
};

export type GetInventoryGfsOrderingFacilityResponses = {
  /**
   * Response List Active Profiles Inventory Gfs Ordering Facility  Get
   *
   * Successful Response
   */
  200: Array<SupplyProfileResponse>;
};

export type GetInventoryGfsOrderingFacilityResponse =
  GetInventoryGfsOrderingFacilityResponses[keyof GetInventoryGfsOrderingFacilityResponses];

export type PostInventoryGfsOrderingFacilityData = {
  body: SupplyProfileCreateDeep;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/facility/";
};

export type PostInventoryGfsOrderingFacilityErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryGfsOrderingFacilityError =
  PostInventoryGfsOrderingFacilityErrors[keyof PostInventoryGfsOrderingFacilityErrors];

export type PostInventoryGfsOrderingFacilityResponses = {
  /**
   * Successful Response
   */
  201: SupplyProfileResponse;
};

export type PostInventoryGfsOrderingFacilityResponse =
  PostInventoryGfsOrderingFacilityResponses[keyof PostInventoryGfsOrderingFacilityResponses];

export type GetInventoryGfsOrderingFacilityByProfileIdData = {
  body?: never;
  path: {
    /**
     * Profile Id
     */
    profile_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/facility/{profile_id}";
};

export type GetInventoryGfsOrderingFacilityByProfileIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingFacilityByProfileIdError =
  GetInventoryGfsOrderingFacilityByProfileIdErrors[keyof GetInventoryGfsOrderingFacilityByProfileIdErrors];

export type GetInventoryGfsOrderingFacilityByProfileIdResponses = {
  /**
   * Successful Response
   */
  200: SupplyProfileResponse;
};

export type GetInventoryGfsOrderingFacilityByProfileIdResponse =
  GetInventoryGfsOrderingFacilityByProfileIdResponses[keyof GetInventoryGfsOrderingFacilityByProfileIdResponses];

export type PutInventoryGfsOrderingFacilityByProfileIdData = {
  body: SupplyProfileUpdateDeep;
  path: {
    /**
     * Profile Id
     */
    profile_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/facility/{profile_id}";
};

export type PutInventoryGfsOrderingFacilityByProfileIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PutInventoryGfsOrderingFacilityByProfileIdError =
  PutInventoryGfsOrderingFacilityByProfileIdErrors[keyof PutInventoryGfsOrderingFacilityByProfileIdErrors];

export type PutInventoryGfsOrderingFacilityByProfileIdResponses = {
  /**
   * Successful Response
   */
  200: SupplyProfileResponse;
};

export type PutInventoryGfsOrderingFacilityByProfileIdResponse =
  PutInventoryGfsOrderingFacilityByProfileIdResponses[keyof PutInventoryGfsOrderingFacilityByProfileIdResponses];

export type GetInventoryGfsOrderingFacilityAdminAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/facility/admin/all";
};

export type GetInventoryGfsOrderingFacilityAdminAllErrors = {
  /**
   * Not found
   */
  404: unknown;
};

export type GetInventoryGfsOrderingFacilityAdminAllResponses = {
  /**
   * Response List All Profiles Inventory Gfs Ordering Facility Admin All Get
   *
   * Successful Response
   */
  200: Array<SupplyProfileResponse>;
};

export type GetInventoryGfsOrderingFacilityAdminAllResponse =
  GetInventoryGfsOrderingFacilityAdminAllResponses[keyof GetInventoryGfsOrderingFacilityAdminAllResponses];

export type PatchInventoryGfsOrderingFacilityByProfileIdDeactivateData = {
  body?: never;
  path: {
    /**
     * Profile Id
     */
    profile_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/facility/{profile_id}/deactivate";
};

export type PatchInventoryGfsOrderingFacilityByProfileIdDeactivateErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchInventoryGfsOrderingFacilityByProfileIdDeactivateError =
  PatchInventoryGfsOrderingFacilityByProfileIdDeactivateErrors[keyof PatchInventoryGfsOrderingFacilityByProfileIdDeactivateErrors];

export type PatchInventoryGfsOrderingFacilityByProfileIdDeactivateResponses = {
  /**
   * Successful Response
   */
  200: SupplyProfileResponse;
};

export type PatchInventoryGfsOrderingFacilityByProfileIdDeactivateResponse =
  PatchInventoryGfsOrderingFacilityByProfileIdDeactivateResponses[keyof PatchInventoryGfsOrderingFacilityByProfileIdDeactivateResponses];

export type PatchInventoryGfsOrderingFacilityByProfileIdReactivateData = {
  body?: never;
  path: {
    /**
     * Profile Id
     */
    profile_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/facility/{profile_id}/reactivate";
};

export type PatchInventoryGfsOrderingFacilityByProfileIdReactivateErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchInventoryGfsOrderingFacilityByProfileIdReactivateError =
  PatchInventoryGfsOrderingFacilityByProfileIdReactivateErrors[keyof PatchInventoryGfsOrderingFacilityByProfileIdReactivateErrors];

export type PatchInventoryGfsOrderingFacilityByProfileIdReactivateResponses = {
  /**
   * Successful Response
   */
  200: SupplyProfileResponse;
};

export type PatchInventoryGfsOrderingFacilityByProfileIdReactivateResponse =
  PatchInventoryGfsOrderingFacilityByProfileIdReactivateResponses[keyof PatchInventoryGfsOrderingFacilityByProfileIdReactivateResponses];

export type GetInventoryGfsOrderingOrdersData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Facility Id
     *
     * Facility ID
     */
    facility_id: number;
  };
  url: "/inventory/gfs_ordering/orders/";
};

export type GetInventoryGfsOrderingOrdersErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingOrdersError =
  GetInventoryGfsOrderingOrdersErrors[keyof GetInventoryGfsOrderingOrdersErrors];

export type GetInventoryGfsOrderingOrdersResponses = {
  /**
   * Response List Orders Inventory Gfs Ordering Orders  Get
   *
   * Successful Response
   */
  200: Array<OrderSummaryResponse>;
};

export type GetInventoryGfsOrderingOrdersResponse =
  GetInventoryGfsOrderingOrdersResponses[keyof GetInventoryGfsOrderingOrdersResponses];

export type GetInventoryGfsOrderingOrdersByOrderIdData = {
  body?: never;
  path: {
    /**
     * Order Id
     */
    order_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/orders/{order_id}";
};

export type GetInventoryGfsOrderingOrdersByOrderIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingOrdersByOrderIdError =
  GetInventoryGfsOrderingOrdersByOrderIdErrors[keyof GetInventoryGfsOrderingOrdersByOrderIdErrors];

export type GetInventoryGfsOrderingOrdersByOrderIdResponses = {
  /**
   * Successful Response
   */
  200: OrderDetailResponse;
};

export type GetInventoryGfsOrderingOrdersByOrderIdResponse =
  GetInventoryGfsOrderingOrdersByOrderIdResponses[keyof GetInventoryGfsOrderingOrdersByOrderIdResponses];

export type GetInventoryGfsOrderingPurchasingUpcomingOrdersData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Facility Id
     *
     * Facility ID
     */
    facility_id: number;
  };
  url: "/inventory/gfs_ordering/purchasing/upcoming-orders";
};

export type GetInventoryGfsOrderingPurchasingUpcomingOrdersErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingPurchasingUpcomingOrdersError =
  GetInventoryGfsOrderingPurchasingUpcomingOrdersErrors[keyof GetInventoryGfsOrderingPurchasingUpcomingOrdersErrors];

export type GetInventoryGfsOrderingPurchasingUpcomingOrdersResponses = {
  /**
   * Response Get Upcoming Orders Inventory Gfs Ordering Purchasing Upcoming Orders Get
   *
   * Successful Response
   */
  200: Array<UpcomingOrderResponse>;
};

export type GetInventoryGfsOrderingPurchasingUpcomingOrdersResponse =
  GetInventoryGfsOrderingPurchasingUpcomingOrdersResponses[keyof GetInventoryGfsOrderingPurchasingUpcomingOrdersResponses];

export type GetInventoryGfsOrderingPurchasingListData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Facility Id
     *
     * Facility ID
     */
    facility_id: number;
    /**
     * Order Date
     *
     * Order date (YYYY-MM-DD)
     */
    order_date: string;
  };
  url: "/inventory/gfs_ordering/purchasing/list";
};

export type GetInventoryGfsOrderingPurchasingListErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingPurchasingListError =
  GetInventoryGfsOrderingPurchasingListErrors[keyof GetInventoryGfsOrderingPurchasingListErrors];

export type GetInventoryGfsOrderingPurchasingListResponses = {
  /**
   * Successful Response
   */
  200: PurchaseListResponse;
};

export type GetInventoryGfsOrderingPurchasingListResponse =
  GetInventoryGfsOrderingPurchasingListResponses[keyof GetInventoryGfsOrderingPurchasingListResponses];

export type PostInventoryGfsOrderingPurchasingRefreshData = {
  body: RefreshOrderRequest;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/purchasing/refresh";
};

export type PostInventoryGfsOrderingPurchasingRefreshErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryGfsOrderingPurchasingRefreshError =
  PostInventoryGfsOrderingPurchasingRefreshErrors[keyof PostInventoryGfsOrderingPurchasingRefreshErrors];

export type PostInventoryGfsOrderingPurchasingRefreshResponses = {
  /**
   * Successful Response
   */
  200: PurchaseListResponse;
};

export type PostInventoryGfsOrderingPurchasingRefreshResponse =
  PostInventoryGfsOrderingPurchasingRefreshResponses[keyof PostInventoryGfsOrderingPurchasingRefreshResponses];

export type PostInventoryGfsOrderingPurchasingUpdateInventoryData = {
  body: InventoryUpdateRequest;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/purchasing/update-inventory";
};

export type PostInventoryGfsOrderingPurchasingUpdateInventoryErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryGfsOrderingPurchasingUpdateInventoryError =
  PostInventoryGfsOrderingPurchasingUpdateInventoryErrors[keyof PostInventoryGfsOrderingPurchasingUpdateInventoryErrors];

export type PostInventoryGfsOrderingPurchasingUpdateInventoryResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type PostInventoryGfsOrderingPurchasingAdjustData = {
  body: AdjustOrderRequest;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/purchasing/adjust";
};

export type PostInventoryGfsOrderingPurchasingAdjustErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryGfsOrderingPurchasingAdjustError =
  PostInventoryGfsOrderingPurchasingAdjustErrors[keyof PostInventoryGfsOrderingPurchasingAdjustErrors];

export type PostInventoryGfsOrderingPurchasingAdjustResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetInventoryGfsOrderingPurchasingCsvByOrderIdData = {
  body?: never;
  path: {
    /**
     * Order Id
     */
    order_id: number;
  };
  query?: never;
  url: "/inventory/gfs_ordering/purchasing/csv/{order_id}";
};

export type GetInventoryGfsOrderingPurchasingCsvByOrderIdErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingPurchasingCsvByOrderIdError =
  GetInventoryGfsOrderingPurchasingCsvByOrderIdErrors[keyof GetInventoryGfsOrderingPurchasingCsvByOrderIdErrors];

export type GetInventoryGfsOrderingPurchasingCsvByOrderIdResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetInventoryGfsOrderingPurchasingOrderCsvData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Facility Id
     */
    facility_id: number;
    /**
     * Order Date
     */
    order_date: string;
  };
  url: "/inventory/gfs_ordering/purchasing/order-csv";
};

export type GetInventoryGfsOrderingPurchasingOrderCsvErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingPurchasingOrderCsvError =
  GetInventoryGfsOrderingPurchasingOrderCsvErrors[keyof GetInventoryGfsOrderingPurchasingOrderCsvErrors];

export type GetInventoryGfsOrderingPurchasingOrderCsvResponses = {
  /**
   * Response List Order Csvs Inventory Gfs Ordering Purchasing Order Csv Get
   *
   * Successful Response
   */
  200: Array<SupplyOrderCsv>;
};

export type GetInventoryGfsOrderingPurchasingOrderCsvResponse =
  GetInventoryGfsOrderingPurchasingOrderCsvResponses[keyof GetInventoryGfsOrderingPurchasingOrderCsvResponses];

export type PostInventoryGfsOrderingPurchasingOrderCsvData = {
  body: BodyUploadOrderCsvInventoryGfsOrderingPurchasingOrderCsvPost;
  path?: never;
  query?: never;
  url: "/inventory/gfs_ordering/purchasing/order-csv";
};

export type PostInventoryGfsOrderingPurchasingOrderCsvErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PostInventoryGfsOrderingPurchasingOrderCsvError =
  PostInventoryGfsOrderingPurchasingOrderCsvErrors[keyof PostInventoryGfsOrderingPurchasingOrderCsvErrors];

export type PostInventoryGfsOrderingPurchasingOrderCsvResponses = {
  /**
   * Successful Response
   */
  200: SupplyOrderCsv;
};

export type PostInventoryGfsOrderingPurchasingOrderCsvResponse =
  PostInventoryGfsOrderingPurchasingOrderCsvResponses[keyof PostInventoryGfsOrderingPurchasingOrderCsvResponses];

export type GetInventoryGfsOrderingPurchasingOrderCsvAnalysisData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Facility Id
     *
     * Facility ID
     */
    facility_id: number;
    /**
     * Order Date
     *
     * Order date (YYYY-MM-DD)
     */
    order_date: string;
  };
  url: "/inventory/gfs_ordering/purchasing/order-csv/analysis";
};

export type GetInventoryGfsOrderingPurchasingOrderCsvAnalysisErrors = {
  /**
   * Not found
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInventoryGfsOrderingPurchasingOrderCsvAnalysisError =
  GetInventoryGfsOrderingPurchasingOrderCsvAnalysisErrors[keyof GetInventoryGfsOrderingPurchasingOrderCsvAnalysisErrors];

export type GetInventoryGfsOrderingPurchasingOrderCsvAnalysisResponses = {
  /**
   * Successful Response
   */
  200: SupplyOrderCsvAnalysis;
};

export type GetInventoryGfsOrderingPurchasingOrderCsvAnalysisResponse =
  GetInventoryGfsOrderingPurchasingOrderCsvAnalysisResponses[keyof GetInventoryGfsOrderingPurchasingOrderCsvAnalysisResponses];
